/**
 * NewWorldDbTable — Main CRM data table component.
 *
 * ARCHITECTURE OVERVIEW (for AI agents & developers):
 * ═══════════════════════════════════════════════════
 * This is the orchestrator component for the contact database table.
 * It manages data fetching, filtering, editing, and delegates rendering
 * to specialized sub-components and hooks.
 *
 * MODULE MAP:
 * ┌─────────────────────────────────────────────────────┐
 * │  NewWorldDbTable (this file) — Orchestrator         │
 * │  ├── useSpyIntelligence    — Spy/Intel hook         │
 * │  ├── WorldDbTableRow       — Single row rendering   │
 * │  ├── WarmupFlowBadge       — Flow status badge      │
 * │  ├── WorldDbTableNav       — Toolbar/nav bar        │
 * │  ├── NotesModal            — Contact notes CRUD     │
 * │  ├── LeadStatusDrawer     — Lead status side panel │
 * │  ├── DecisionMakersModal   — Decision makers CRUD   │
 * │  ├── SocialEditModal       — Social links editor    │
 * │  ├── IntelligenceModal     — Signal details view    │
 * │  ├── FlowSelectModal       — Warmup flow picker     │
 * │  ├── EmailPopup / EmailAiPopup / BulkEmailPopup     │
 * │  ├── MassSpyPopup          — Bulk intel progress    │
 * │  ├── AiCleanerPopup        — AI contact cleaner     │
 * │  ├── SpyDataPreview        — Intel result review    │
 * │  ├── SimilarContactsPanel  — Similarity results     │
 * │  └── ActionCenter          — Quick-action bar       │
 * └─────────────────────────────────────────────────────┘
 *
 * DATA FLOW:
 *   Firestore (`World/{userId}/db`) → fetchData() → dbData state
 *   → displayRows (useMemo: filtered + sorted) → WorldDbTableRow[]
 *
 * KEY STATE GROUPS (see section markers below):
 *   §DATA     — dbData, loading, pagination
 *   §FILTERS  — sort, status, import, advanced, search
 *   §UI       — modals, popups, selection, editing
 *   §SETTINGS — warmup flows, email templates, spy settings
 *
 * EXTENDING THIS COMPONENT:
 *   - New column?      → Add to columnOrder + availableFields, handle in WorldDbTableRow
 *   - New modal?       → Create MyModal.js, add state to §UI, render at bottom of JSX
 *   - New filter?      → Add to §FILTERS state, apply in fetchData() or displayRows
 *   - New bulk action?  → Add handler, pass to WorldDbTableNav
 */
import React, { useState, useMemo, useEffect, useRef, useCallback, useDeferredValue } from "react";
import { database, firestore, functions, auth } from "../firebaseConfig";
import useTaskActionListener from "./tasks/useTaskActionListener";
import { TABLE_TASK_ACTIONS, CONTACT_PATCH_EVENT } from "./tasks/taskUtils";
import { ref, onValue, get, update } from "firebase/database";
import { httpsCallable } from "firebase/functions";
import { 
  doc, 
  updateDoc, 
  deleteDoc,
  deleteField,
  setDoc,
  collection,
  query,
  orderBy,
  startAfter,
  limit,
  getDocs,
  getDoc,
  where,
  getCountFromServer,
  writeBatch,
  arrayUnion
} from 'firebase/firestore';
import "./WorldDbTable.css";
import WorldDbTableNav from './WorldDbTableNav';
import DuplicateInlineHint from './DuplicateInlineHint';
import DuplicateMergeModal from './DuplicateMergeModal';
import DuplicateActionModal from './DuplicateActionModal';
import DuplicateFlowStage from './DuplicateFlowStage';
import ContactDetailModal from './ContactDetailModal';
import OutreachJobPausedModal from './OutreachJobPausedModal';
import useDuplicateAlerts from './useDuplicateAlerts';
import { normalizeEmailForComparison } from '../shared/contactIdentity';
import EmailAiPopup from './EmailAiPopup';
import EmailPopup from './EmailPopup';
import BulkEmailPopup from './BulkEmailPopup';
import MassSpyPopup from './MassSpyPopup';
import AiCleanerPopup from './AiCleanerPopup';
import SpyDataPreview from './SpyDataPreview';
import useDebounce from "../hooks/use-debounce";
import { useActiveWorld } from "../hooks/useActiveWorld";
import useCreditBalance from "../hooks/useCreditBalance";
import useResearchAccess from "../hooks/useResearchAccess";
import { startFlowForContact, completeFlowStep, stopFlowForContact } from './warmupFlowUtils';
import { logOutcomeAnalytics, logReplyAnalytics } from './analyticsTracker';
import { generateTrigrams, buildSearchTerms } from './searchUtils';
import { dedupFieldPatch } from '../shared/contactIdentity';
import { computeAndStoreSignals, recordSignalCorrection, enqueueForClassification, setOnSignalsComputed, signalsAreFresh, resetBudgetExceededFlag, resetLazyProcessed } from './leadSignalEngine';
import ActionCenter from './ActionCenter';
import { findSimilarContacts } from './similarityEngine';
import SimilarContactsPanel from './SimilarContactsPanel';
import NotesModal from './NotesModal';
import LeadStatusDrawer from './LeadStatusDrawer';
import { createPrivateNote, hasNoteContent } from './NotesActivityFields';
import DecisionMakersModal from './DecisionMakersModal';
import SocialEditModal from './SocialEditModal';
import IntelligenceModal from './IntelligenceModal';
import FlowSelectModal from './FlowSelectModal';
import FlowPickerList from './FlowPickerList';
import useSpyIntelligence from './useSpyIntelligence';
import { useTablePrefs } from './useTablePrefs';
import useWebsiteAnalyzer from './useWebsiteAnalyzer';
import usePotentialAnalyzer from './usePotentialAnalyzer';
import { calculateWebsiteAnalyzerCost } from '../config/creditPlans';
import SpySelectorPopup from './SpySelectorPopup';
import InstagramResearchModal from './InstagramResearchModal';
import SeoAnalyzerModal from './SeoAnalyzerModal';
import BulkResearchModal from './BulkResearchModal';
import WorldDbTableRow, { parseSocials } from './WorldDbTableRow';
import AdvancedFiltersPanel from './AdvancedFiltersPanel';
import PreparedMailsStrip from './PreparedMailsStrip';
import IntelligenceErrorBoundary from './IntelligenceErrorBoundary';
import { STRIKETHROUGH_TAGS, getAllContactEmails, parseDecisionMakers } from '../shared/enums';
import { guessNameFromEmail } from './notesContactSuggest';
import { contactMatchesPoolLists, jobCanDiscoverEmail } from './contactPoolLists';
import { lookupBlacklistForEmails, isEmailBlacklisted } from '../shared/blacklistUtils';
import { describeEdit, logContactActivity, logOutreachEnrollment } from '../shared/contactActivityLog';
import { resolveTemplateForContact, resolveFlowEmailStep } from './EmailAiModes/emailTrackingUtils';
import useCustomFields from '../hooks/useCustomFields';

const normalizeLinkedin = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:/i.test(raw)) return raw;
  if (/linkedin\.com/i.test(raw)) return `https://${raw.replace(/^\/\//, "")}`;
  return `https://www.linkedin.com/in/${raw.replace(/^@/, "")}`;
};

const linkedinHref = (contact) => {
  const direct = normalizeLinkedin(contact?.lead_linkedin);
  if (direct) return direct;
  let people = contact?.lead_decision_makers;
  if (typeof people === "string") {
    try { people = JSON.parse(people); } catch (_) { people = []; }
  }
  if (Array.isArray(people)) {
    for (const person of people) {
      const url = normalizeLinkedin(person?.linkedinUrl || person?.linkedin);
      if (url) return url;
    }
  }
  const name = [contact?.lead_firstname, contact?.lead_lastname, contact?.business_name].filter(Boolean).join(" ");
  return name
    ? `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(name)}`
    : "https://www.linkedin.com/";
};

const FAVORITE_TAG = { id: 'favorite', name: 'Favorite', color: '#6366f1', icon: 'fa-heart' };

// Number of skeleton placeholder rows shown while loading. When real data is
// ready, these placeholders are swapped for real rows one-by-one top-to-bottom
// (see revealCount) so the skeleton is replaced in real time instead of
// blanking out before the rows fade in.
const SKELETON_COUNT = 12;
const REVEAL_STEP_MS = 35;
// Max time the staggered reveal waits for the blacklist filter to settle before
// revealing anyway, so a slow/failed lookup can never block the list.
const REVEAL_SETTLE_MAX_WAIT_MS = 700;

// Per-account cache of the natural column widths (in px). We measure the widths
// the browser computes from real data (so they're never invented) and replay
// them on the next load via a <colgroup> + table-layout:fixed. That keeps the
// skeleton and the real rows at the exact same widths, so columns stop sliding
// around as content loads — without changing the column sizes themselves.
const COLWIDTH_CACHE_PREFIX = "wdb_colwidths_";

// Floor for columns whose content is fixed-size icons that can neither shrink nor
// wrap. Mirrors the min-width of .socials-cell / .decision-makers-cell, which the
// browser ignores once the layout is frozen to table-layout:fixed.
const MIN_FROZEN_COL_WIDTHS = {
  // Score badge (27) + gap + row marker + cell padding. Cached widths are
  // measured from the "Nr." header, so without this floor table-layout:fixed
  // keeps the column at ~35px and clips the circle.
  rowNumber: 72,
  lead_socials: 308,
  lead_decision_makers: 266,
};
// Mirrors the min-width of .worldDbTable in WorldDbTable.css.
const TABLE_MIN_WIDTH = 1200;

function readColWidthCache(userId) {
  if (!userId || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(`${COLWIDTH_CACHE_PREFIX}${userId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function writeColWidthCache(userId, widths) {
  if (!userId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(`${COLWIDTH_CACHE_PREFIX}${userId}`, JSON.stringify(widths));
  } catch {
    /* ignore quota / unavailable */
  }
}

// Mapping der Spaltennamen für lesbare Überschriften
const BASE_AVAILABLE_FIELDS = {
  lead_email: "Email",
  lead_telephone: "Telephone",
  lead_address: "Address",
  lead_city: "City",
  lead_postcode: "Postcode",
  lead_country: "Country",
  business_name: "Business Name",
  lead_website: "Website",
  lead_socials: "Socials",
  lead_rating: "Rating",
  lead_reviews: "Reviews",
  lead_instagram_followers: "Followers",
  lead_link: "Link",
  lead_image: "Image",
  lead_description: "Description",
  lead_stars: "Stars",
  lead_reply: "Has Replied",
  lead_decision_makers: "Decision Makers",
  lead_score: "Lead Score",
  lead_pacing: "Pacing",
  lead_template: "Templates",
  timestamp: "Added",
  import_name: "Import"
};

const ALREADY_PLACED_KEYS = [
  "lead_type", "lead_description", "business_name", "lead_email",
  "lead_telephone", "lead_website", "lead_socials", "lead_instagram",
  "lead_facebook", "lead_tiktok", "lead_linkedin", "lead_decision_makers",
  "lead_score", "lead_pacing", "lead_template", "lead_reply",
  "lead_rating", "lead_reviews", "lead_instagram_followers"
];

function buildColumnOrder(baseFields, customFieldKeys, savedOrder) {
  const PREFIX = ["selectCheckbox", "rowNumber", "warmupFlow", "contacted", "aiMail", "spy"];
  const SUFFIX = ["websiteAnalysis", "intelligence", "lead_pacing", "lastEdited"];

  if (!savedOrder || savedOrder.length === 0) {
    return [
      ...PREFIX,
      "business_name", "lead_description", "lead_email", "lead_telephone", "lead_website",
      "lead_rating", "lead_instagram_followers", "lead_reviews", "lead_socials", "lead_decision_makers",
      ...Object.keys(baseFields).filter((key) => !ALREADY_PLACED_KEYS.includes(key)),
      ...customFieldKeys,
      "lead_template", "lead_reply", "lead_score",
      ...SUFFIX
    ];
  }

  const allReorderableKeys = new Set([
    "business_name", "lead_description", "lead_email", "lead_telephone", "lead_website",
    "lead_rating", "lead_instagram_followers", "lead_reviews", "lead_socials", "lead_decision_makers",
    ...Object.keys(baseFields).filter((key) => !ALREADY_PLACED_KEYS.includes(key)),
    ...customFieldKeys,
    "lead_template", "lead_reply", "lead_score"
  ]);

  const orderedKeys = savedOrder.filter(key => allReorderableKeys.has(key));
  const orderedSet = new Set(orderedKeys);
  const missingKeys = [...allReorderableKeys].filter(key => !orderedSet.has(key));

  return [...PREFIX, ...orderedKeys, ...missingKeys, ...SUFFIX];
}

const CLIENT_SORT_FIELDS = new Set([
  "lead_description", "lead_telephone", "lead_website",
  "lead_socials", "lead_decision_makers", "lead_template", "lead_reply",
  "contacted",
]);

function getClientSortValue(row, field, customFieldType) {
  const raw = row[field];

  switch (field) {
    case "contacted": {
      const ts = row.lead_last_email && row.lead_last_email.timestamp
        ? parseInt(row.lead_last_email.timestamp, 10)
        : 0;
      return isNaN(ts) ? 0 : ts;
    }
    case "lead_telephone": {
      const digits = String(raw || "").replace(/[^0-9]/g, "");
      return digits ? parseInt(digits, 10) : 0;
    }
    case "lead_website": {
      return String(raw || "")
        .toLowerCase()
        .replace(/^https?:\/\//, "")
        .replace(/^www\./, "")
        .replace(/\/$/, "");
    }
    case "lead_socials": {
      let socials = {};
      try {
        if (raw && typeof raw === "string") socials = JSON.parse(raw);
        else if (raw && typeof raw === "object") socials = raw;
      } catch (e) { /* ignore */ }
      return Object.values(socials).filter((v) => v && String(v).trim()).length;
    }
    case "lead_decision_makers": {
      let dms = [];
      try {
        if (raw && typeof raw === "string") dms = JSON.parse(raw);
        else if (Array.isArray(raw)) dms = raw;
      } catch (e) { /* ignore */ }
      return dms.length;
    }
    case "lead_reply":
      return raw === true ? 1 : 0;
    default: {
      if (customFieldType === "number") {
        return parseFloat(raw) || 0;
      }
      return String(raw || "").toLowerCase();
    }
  }
}

function clientSideCompare(aVal, bVal, direction) {
  const isNum = typeof aVal === "number" && typeof bVal === "number";
  if (isNum) {
    const diff = aVal - bVal;
    return direction === "asc" ? diff : -diff;
  }
  const aStr = String(aVal);
  const bStr = String(bVal);
  // Natural sort: erkennt Zahlen-Substrings innerhalb von Strings und
  // sortiert sie numerisch. Behebt das Problem dass "8" lexikografisch
  // nach "82" einsortiert wurde wenn ein Custom Field als "text" markiert
  // ist aber numerische Werte enthält. Funktioniert auch bei gemischten
  // Strings wie "Produkt 8" vs "Produkt 42" — die Zahlen werden korrekt
  // verglichen, der Text-Teil normal alphabetisch.
  const cmp = aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' });
  return direction === "asc" ? cmp : -cmp;
}

const PAGE_SIZE = 100;

// Follow-up-Stufen: hier dürfen Kontakte, die bereits geantwortet haben
// (lead_reply === true), NICHT auftauchen — sonst riskieren wir, ein
// Follow-up an jemanden zu schicken, der schon geantwortet hat. Solche
// Kontakte erreicht man stattdessen über den eigenen "Replied"-Filter.
const FOLLOWUP_STAGES = new Set([
  'Introduced',
  'First Follow-up',
  'Second Follow-up',
  'Third Follow-up',
]);

// Blockierte Kontakte (Inbox-Block / Blacklist / globalOptOut, z. B. auch durch
// Hard-Bounces). Solche Kontakte dürfen NIRGENDS in der World DB auftauchen —
// weder in den Follow-up-Stufen noch in irgendeiner anderen Ansicht. Damit ist
// garantiert, dass kein (Follow-up-)Mail mehr versehentlich an einen blockierten
// Kontakt rausgeht. Aufheben/Verwalten passiert über die Blacklist-Einstellungen,
// nicht über diese Tabelle.
const isBlockedContact = (row) =>
  row?.globalOptOut === true ||
  row?.lead_contacted === 'Blacklisted' ||
  row?.lead_status === 'Blacklisted';

// Server-side flow-filter constraints. Mirrors the client-side flow filter in
// displayRows using live fields, so both stay perfectly in sync:
//   flow_sort_priority: 2 = active flow, 3 = no flow, 4 = flow ended/disqualified
//   (invariant — always set on every contact, see WorldDbApi/Uploader/Creator)
//   warmupFlow.flowId / warmupFlow.currentStepIndex = the live active-flow state
// Returns [] when no flow filter is active (nothing is added to the query).
function buildFlowConstraints(flowFilter, flowStepFilter) {
  if (!flowFilter || flowFilter === 'all') return [];
  // "No Flow" = no active flow (includes contacts that finished a flow), which
  // matches the client-side `!hasFlow` predicate → priority 3 (never) or 4 (ended).
  if (flowFilter === 'no_flow') return [where('flow_sort_priority', 'in', [3, 4])];
  // "In Any Flow" = an active flow is running → priority 2.
  if (flowFilter === 'has_flow') return [where('flow_sort_priority', '==', 2)];
  // "Finished" = shows the "FIN" badge specifically — a flow that ran all the
  // way through to a plain "completed" end node (not disqualified / not
  // interested / wrong contact / converted / nurture, which get their own
  // badges — see FLOW_END_CONFIG in FlowSelectModal.js).
  if (flowFilter === 'finished') return [where('flowEndStatus', '==', 'completed')];
  // Specific flow that has ended → identified by the flow-completed tag written
  // on completion (tags.flow_completed_<flowId>.flowId === flowId).
  if (flowStepFilter === 'flow_ended') {
    return [where(`tags.flow_completed_${flowFilter}.flowId`, '==', flowFilter)];
  }
  // Specific active flow (optionally narrowed to a single step).
  const constraints = [where('warmupFlow.flowId', '==', flowFilter)];
  if (flowStepFilter && flowStepFilter !== 'all') {
    const stepIndex = parseInt(flowStepFilter, 10);
    if (!Number.isNaN(stepIndex)) {
      constraints.push(where('warmupFlow.currentStepIndex', '==', stepIndex));
    }
  }
  return constraints;
}

const NewWorldDbTable = ({ 
  userId, 
  setShowUploader, 
  setShowCreator,
  totalDatabaseCount,
  contactedToday,
  onEmailResult,
  onReload: parentOnReload,
  reloadFlag
}) => {
  // ── §DATA — Core table data & pagination ──
  const [dbData, setDbData] = useState([]);
  // Always-fresh mirror of dbData for use inside async callbacks (e.g. the
  // research→email-finder combo) that would otherwise close over stale state.
  const dbDataRef = useRef([]);
  const [editedData, setEditedData] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMoreData, setHasMoreData] = useState(true);
  const itemsToLoadRef = useRef(PAGE_SIZE);
  const autoLoadAttemptRef = useRef(0);
  // Blacklist (Email- + Domain-Sets) — wird genutzt, um blockierte Kontakte aus
  // der Tabelle auszublenden. Fängt auch Kontakte einer geblockten Domain ab,
  // die (z. B. nach späterem Import) noch keine globalOptOut-/Blacklisted-Flags
  // tragen. So taucht garantiert kein blockierter Kontakt in der DB auf.
  const [blacklistSets, setBlacklistSets] = useState(null);
  // The `loadedEmailsKey` the current `blacklistSets` was resolved for. Lets the
  // reveal animation wait until the blacklist filter matches the data actually
  // on screen, so the first reveal already shows the final (filtered) set.
  const [blacklistResolvedKey, setBlacklistResolvedKey] = useState(null);

  // ── §FILTERS — Sort, status, import, search, advanced ──
  // View-Einstellungen (Sortierung, Filter, Suche, Auswahl von Listen/Gruppen)
  // werden pro World 30 Min in localStorage gecacht, damit sie einen Page-Refresh
  // bzw. Tab-Wechsel überleben und nicht jedes Mal neu gesetzt werden müssen.
  const { initialPrefs, persist: persistTablePrefs } = useTablePrefs(userId);
  const DEFAULT_ADVANCED_FILTERS = {
    city: '', has_email: 'any', has_website: 'any', has_instagram: 'any',
    bounce_reduction: 'any',
    range_field: 'lead_rating', range_min: '', range_max: '',
  };
  const [sortField, setSortField] = useState(initialPrefs.sortField ?? "timestamp");
  const [sortDirection, setSortDirection] = useState(initialPrefs.sortDirection ?? "desc");
  const [statusFilter, setStatusFilter] = useState(initialPrefs.statusFilter ?? "Not Contacted");
  const [importFilter, setImportFilter] = useState(initialPrefs.importFilter ?? "all");
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [flowFilter, setFlowFilter] = useState(initialPrefs.flowFilter ?? "all");
  const [flowStepFilter, setFlowStepFilter] = useState(initialPrefs.flowStepFilter ?? "all");
  const [importOptions, setImportOptions] = useState([]);
  const [filteredCount, setFilteredCount] = useState(null);
  // Count of contacts with a backend-prepared flow email (drives the
  // "X Emails Prepared" quick-action button in the nav). Refreshed on load,
  // whenever data reloads, and after the bulk sender closes.
  const [preparedCount, setPreparedCount] = useState(0);
  const [preparedCountTrigger, setPreparedCountTrigger] = useState(0);
  const [isLoadingPrepared, setIsLoadingPrepared] = useState(false);
  const [noFilterResults, setNoFilterResults] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(initialPrefs.showAdvancedFilters ?? false);
  const [advancedFilters, setAdvancedFilters] = useState(
    initialPrefs.advancedFilters ?? DEFAULT_ADVANCED_FILTERS
  );
  const [segmentFilter, setSegmentFilter] = useState(initialPrefs.segmentFilter ?? null);
  const [searchTerm, setSearchTerm] = useState(initialPrefs.searchTerm ?? "");
  const debouncedSearchTerm = useDebounce(searchTerm, 400);
  const [isSearching, setIsSearching] = useState(false);
  const [needsMigration, setNeedsMigration] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState(0);
  const flowSortReadyRef = useRef(false);
  // Erst laden, wenn die Flow-Sort-Prüfung abgeschlossen ist. Verhindert den
  // doppelten Initial-Fetch mit zwei unterschiedlichen Sortierungen (timestamp
  // vs. flow_sort_priority), durch den sich beim Reload Kontakte scheinbar
  // "mitten in die Liste" einschoben.
  const [flowSortDecided, setFlowSortDecided] = useState(false);

  // ── §UI — Modals, popups, selection, scroll, editing ──
  const [emailPopupData, setEmailPopupData] = useState({ show: false, rowData: null, index: null });
  const [similarPanel, setSimilarPanel] = useState({ show: false, target: null, results: [], loading: false });
  const [intelModal, setIntelModal] = useState(null);

  // ── §EFFECTS — Data loading triggers & subscriptions ──

  useEffect(() => {
    resetBudgetExceededFlag();
    resetLazyProcessed();
    setOnSignalsComputed((updates) => {
      if (!updates || updates.length === 0) return;
      setDbData(prev => prev.map(row => {
        const match = updates.find(u => u.contactId === row.id);
        if (match) return { ...row, leadSignals: match.signals };
        return row;
      }));
    });
    return () => setOnSignalsComputed(null);
  }, [userId]);

  useEffect(() => {
    if (!userId || !dbData || dbData.length === 0) return;
    const INITIAL_BATCH = 100;
    dbData.slice(0, INITIAL_BATCH).forEach(row => {
      if (row && row.id && !signalsAreFresh(row.leadSignals, row)) {
        enqueueForClassification(userId, row.id, row);
      }
    });
  }, [dbData, userId]);

  // Stelle ScrollPosition wieder her, wenn Popup geschlossen wurde
  useEffect(() => {
    if (!emailPopupData.show) {
      const id = setTimeout(() => {
        restoreScrollPosition();
      }, 50); // kleiner Delay, bis DOM umgebaut ist
      return () => clearTimeout(id);
    }
  }, [emailPopupData.show]);

  // §UI continued — Selection & delete
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedRows, setSelectedRows] = useState({});
  const [lastSelectedRowId, setLastSelectedRowId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ show: false, count: 0, onConfirm: null, emails: [], contactIds: [] });
  const [blacklistOnDelete, setBlacklistOnDelete] = useState(false);

  // §UI continued — Scroll & inline editing
  const tableContainerRef = useRef(null);
  const scrollPositionRef = useRef({ top: 0, left: 0 });
  const [pendingEdits, setPendingEdits] = useState({});
  const [activeEditField, setActiveEditField] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const currentEditValue = useRef('');

  // Phase E-Fix (2026-08, Inline-Hint): Wenn ein Inline-Edit auf lead_email
  // eine E-Mail schreibt, die bereits an einem anderen (nicht-gemergten)
  // Kontakt hängt, zeigen wir sofort einen kleinen Toast — noch bevor der
  // Server-Trigger nachliefert. Der persistente Alert kommt trotzdem, aber
  // der User bekommt hier den Kontext direkt am Ort des Fehlers.
  // Struktur: { email, matches: [{id, businessName}], rowId } | null
  const [duplicateHint, setDuplicateHint] = useState(null);
  const [inlineMergeCtx, setInlineMergeCtx] = useState(null); // { contactIds: string[] }

  // Phase E-Fix (2026-08, Row-Highlight): live-Feed offener Alerts +
  // Kontakt→Alert-Map. Jeder involvierte Kontakt (Anchor oder in others)
  // findet über die Map "seinen" Alert und bekommt in der Tabelle eine
  // rote Markierung + Warn-Button-Icon in der rowNumber-Zelle.
  const { openAlerts: duplicateAlerts } = useDuplicateAlerts(userId);
  const duplicateAlertMap = useMemo(() => {
    const map = new Map();
    (duplicateAlerts || []).forEach((alert) => {
      const ids = [alert.currentContactId, ...(Array.isArray(alert.otherContactIds) ? alert.otherContactIds : [])];
      ids.forEach((id) => {
        if (!id) return;
        // Neuere Alerts (Order: createdAt desc) gewinnen — hier bleibt der
        // erste stehen, weil die Query bereits desc-sortiert liefert.
        if (!map.has(id)) map.set(id, alert);
      });
    });
    return map;
  }, [duplicateAlerts]);
  const [duplicateActionCtx, setDuplicateActionCtx] = useState(null); // alert-Doc
  const [dupFlowStep, setDupFlowStep] = useState('action');
  const [dupFlowBusy, setDupFlowBusy] = useState(false);
  const dupFlowTimer = useRef(null);

  useEffect(() => {
    if (duplicateActionCtx && !inlineMergeCtx) setDupFlowStep('action');
    else if (inlineMergeCtx && !duplicateActionCtx) setDupFlowStep('merge');
  }, [duplicateActionCtx, inlineMergeCtx]);

  useEffect(() => {
    if (!inlineMergeCtx || !duplicateActionCtx) return undefined;
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setDupFlowStep('merge'));
    });
    return () => cancelAnimationFrame(id);
  }, [inlineMergeCtx, duplicateActionCtx]);
  const [contactDetailCtx, setContactDetailCtx] = useState(null); // contact-Doc für ContactDetailModal
  const handleOpenDuplicateAction = useCallback((rowId) => {
    const alert = duplicateAlertMap.get(rowId);
    if (alert) setDuplicateActionCtx(alert);
  }, [duplicateAlertMap]);
  // Wird vom Duplicate-Alerts-Panel aufgerufen: das Panel schließt sich und
  // wir öffnen das *gleiche* DuplicateActionModal wie beim Klick aufs
  // Warn-Icon in der Row. So bleibt der Fix-Flow überall identisch.
  const handleOpenDuplicateAlertFromPanel = useCallback((alertDoc) => {
    if (alertDoc) setDuplicateActionCtx(alertDoc);
  }, []);
  // Für "Öffnen" im DuplicateActionModal: statt ins Notes-Modal zu springen,
  // ein dediziertes Detail-Modal über dem Konflikt-Modal öffnen. Der User
  // bleibt so im Konflikt-Kontext und sieht die Rohdaten des Kontakts.
  const handleOpenContactDetail = useCallback(async (contactId) => {
    if (!contactId) return;
    let contact = dbData.find((r) => r.id === contactId);
    if (!contact && userId) {
      try {
        const snap = await getDoc(doc(firestore, `World/${userId}/db/${contactId}`));
        if (snap.exists()) contact = { id: snap.id, ...snap.data() };
      } catch (err) {
        console.warn('[openContactDetail] fetch failed:', err && err.message);
      }
    }
    if (contact) setContactDetailCtx(contact);
  }, [dbData, userId]);

  // §UI continued — Bulk operation popups
  const [isAiManagerOpen, setIsAiManagerOpen] = useState(false);
  const [isAutoSendOpen, setIsAutoSendOpen] = useState(false);
  const [bulkSelectedRows, setBulkSelectedRows] = useState([]);
  const [bulkContactsData, setBulkContactsData] = useState([]);
  const [isAiCleanerOpen, setIsAiCleanerOpen] = useState(false);
  const [cleanerResults, setCleanerResults] = useState(null);

  // §UI continued — Modal open/close state (content handled by extracted components)
  const [decisionMakersModal, setDecisionMakersModal] = useState({ isOpen: false, contactData: null, decisionMakers: [] });
  const [socialEditModal, setSocialEditModal] = useState({ isOpen: false, rowId: null, socials: {} });
  const [notesModal, setNotesModal] = useState({ isOpen: false, contact: null });
  const [leadStatusId, setLeadStatusId] = useState(null);
  const [flowSelectModal, setFlowSelectModal] = useState(null);
  const [bulkFlowModal, setBulkFlowModal] = useState(false);
  const [bulkTemplateAssigning, setBulkTemplateAssigning] = useState(false);
  const [templateModal, setTemplateModal] = useState({ isOpen: false, contactId: null, isBulk: false, stages: { intro: '', follow_up_1: '', follow_up_2: '', follow_up_3: '' } });
  const [spySelectorRow, setSpySelectorRow] = useState(null);
  const [igResearchContact, setIgResearchContact] = useState(null);
  const [seoResearchContact, setSeoResearchContact] = useState(null);
  const [bulkResearchModal, setBulkResearchModal] = useState({ isOpen: false, contacts: [] });
  // Combo flow (Web Research → DM Email Finder): holds the contact ids to run
  // the DM Email Finder on once Web Research has applied its fresh data.
  const comboPendingRef = useRef(null);
  // Smart Funnel: when the funnel hands off to the Web→DM combo it wants a true
  // one-click run, so the Mass Spy review is auto-confirmed instead of waiting
  // for a manual click. The normal "Research + Find Emails" combo keeps its review.
  const funnelAutoConfirmRef = useRef(false);
  // Whole-run stats accumulated by the Ultimate Research funnel (removed,
  // qualified …). Carried across the Web→DM handoff so the final completion
  // modal reflects the entire research, not just the DM Email Finder leg.
  const funnelStatsRef = useRef(null);
  useEffect(() => { dbDataRef.current = dbData; }, [dbData]);

  // ── §SETTINGS — Firebase-synced configuration ──
  const [ownerHasAutoSendButton, setOwnerHasAutoSendButton] = useState(false);
  // Lizenz vs. Konfiguration: `research` sagt, ob der Account das Tool
  // überhaupt haben darf, `seoKeyReady`/paSettings, ob es startklar ist.
  const { research, researchLoading, researchContext } = useResearchAccess();
  const [seoKeyReady, setSeoKeyReady] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [warmupFlows, setWarmupFlows] = useState([]);
  const [completingFlowStep, setCompletingFlowStep] = useState({});
  const [bulkFlowStarting, setBulkFlowStarting] = useState(false);
  const [hasOutreachJobs, setHasOutreachJobs] = useState(false);
  const [outreachJobs, setOutreachJobs] = useState({});
  const [outreachJobModal, setOutreachJobModal] = useState({ isOpen: false, assigning: false });
  const DEFAULT_FLOW_SORT_ORDER = ['email_ready', 'other_task', 'no_flow', 'delay', 'ended', 'disqualified'];
  const [flowSortOrder, setFlowSortOrder] = useState(DEFAULT_FLOW_SORT_ORDER);
  const { features: teamFeatures, isOwner, currentUserId } = useActiveWorld();
  const { balance: creditBalance } = useCreditBalance(userId);
  const { customFields, savedColumnOrder, loading: customFieldsLoading } = useCustomFields(userId);

  // Tracks whether we've completed at least one full load (rows + column
  // settings). Used only to make sure the cold-start full-screen loader never
  // reappears once we're able to render the real (skeleton-capable) table.
  const [initialReady, setInitialReady] = useState(false);
  useEffect(() => {
    if (!initialReady && !loading && !customFieldsLoading) {
      setInitialReady(true);
    }
  }, [initialReady, loading, customFieldsLoading]);

  const availableFields = useMemo(() => {
    const fields = { ...BASE_AVAILABLE_FIELDS };
    customFields.forEach((cf) => { fields[cf.key] = cf.label; });
    return fields;
  }, [customFields]);

  const columnOrder = useMemo(
    () => buildColumnOrder(BASE_AVAILABLE_FIELDS, customFields.map((cf) => cf.key), savedColumnOrder),
    [customFields, savedColumnOrder]
  );

  const customFieldTypes = useMemo(() => {
    const map = {};
    customFields.forEach((cf) => { map[cf.key] = cf.fieldType || "text"; });
    return map;
  }, [customFields]);

  const tableRef = useRef(null);
  // Scroll-Lock: blockiert Überschreiben der gespeicherten Scroll-Position, solange das Popup offen
  const scrollLockedRef = useRef(false);

  const handleEmailResult = useCallback(
    (status, message, ...rest) => {
      if (!message) {
        if (onEmailResult) onEmailResult(status, message, ...rest);
        return;
      }
      // Speichere die Scroll-Position vor jeder Aktualisierung
      saveScrollPosition();
      // Kontakt wurde gelöscht -> entferne sofort aus der Tabelle
      if (status === 'contact-deleted' && typeof message === 'string') {
        setDbData(prev => prev.filter(item => item && item.id !== message));
        restoreScrollPosition();
        return;
      }
      // Status-Updates direkt in die Tabelle patchen
      if (status === 'status-update' && message.id) {
        setDbData(prevData => {
          const idx = prevData.findIndex(item => item && item.id === message.id);
          if (idx === -1) return prevData;
          const newData = [...prevData];
          newData[idx] = {
            ...newData[idx],
            [message.field]: message.value,
            ...(message.field === 'lead_contacted' ? { lead_status: message.value } : {}),
            ...(message.field === 'lead_status' ? { lead_contacted: message.value } : {}),
            lead_last_email: {
              ...newData[idx].lead_last_email,
              timestamp: message.timestamp,
              status: message.value,
            },
            updated_at: message.timestamp,
            last_contact_date: new Date(message.timestamp).toISOString(),
          };
          return newData;
        });
        setEditedData(prev => ({ ...prev, [`${message.id}-${message.field}`]: message.value }));
      }
      // Flow-Updates: warmupFlow nach Flow-Advancement sofort patchen
      if (status === 'flow-update' && message.id) {
        setDbData(prevData => {
          const idx = prevData.findIndex(item => item && item.id === message.id);
          if (idx === -1) return prevData;
          const newData = [...prevData];
          newData[idx] = {
            ...newData[idx],
            warmupFlow: message.warmupFlow,
            flow_sort_priority: message.flowEndStatus ? 4 : (message.warmupFlow ? 2 : newData[idx].flow_sort_priority),
            ...(message.flowEndStatus ? { flowEndStatus: message.flowEndStatus, flowEndedAt: Date.now() } : {}),
            ...(message.autoAppliedTags ? { tags: { ...(newData[idx].tags || {}), ...message.autoAppliedTags } } : {}),
            ...((['disqualified', 'not_interested', 'wrong_contact'].includes(message.flowEndStatus)) ? { isDisqualified: true } : {}),
            updated_at: Date.now(),
          };
          return newData;
        });
      }
      // Mark DM as contacted when email sent to a DM's address
      if (status === 'success' && rest[0]?.id && rest[2]) {
        const contactId = rest[0].id;
        const recipientEmail = rest[2];
        setDbData(prev => {
          const row = prev.find(r => r && r.id === contactId);
          if (!row) return prev;
          try {
            let dms = [];
            const raw = row.lead_decision_makers;
            if (raw && typeof raw === 'string') dms = JSON.parse(raw);
            else if (Array.isArray(raw)) dms = raw;
            const dmIdx = dms.findIndex(dm => dm.email && dm.email.toLowerCase() === recipientEmail.toLowerCase());
            if (dmIdx === -1 || dms[dmIdx].contacted) return prev;
            dms = [...dms];
            dms[dmIdx] = { ...dms[dmIdx], contacted: true, contactedAt: new Date().toISOString() };
            const dmJson = JSON.stringify(dms);
            const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
            updateDoc(contactRef, { lead_decision_makers: dmJson }).catch(() => {});
            return prev.map(r => r.id === contactId ? { ...r, lead_decision_makers: dmJson } : r);
          } catch (_) { return prev; }
        });
      }
      if (onEmailResult) onEmailResult(status, message, ...rest);
      setTimeout(restoreScrollPosition, 100);
    },
    [onEmailResult, userId]
  );

  // Setze isSearching auf true wenn searchTerm sich ändert, false wenn debounce fertig
  useEffect(() => {
    if (searchTerm !== debouncedSearchTerm) {
      setIsSearching(true);
    } else {
      setIsSearching(false);
    }
  }, [searchTerm, debouncedSearchTerm]);

  useEffect(() => {
    if (!userId) return;
    
    const ownerRef = ref(database, `Users/${userId}/hasAutoSendButton`);
    const unsubscribe = onValue(ownerRef, (snapshot) => {
      const hasButton = snapshot.exists() ? snapshot.val() === true : false;
      setOwnerHasAutoSendButton(hasButton);
    });
    
    return () => unsubscribe();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    // Startklar ist der SEO Analyzer erst mit hinterlegtem Sistrix-Key. Das ist
    // unabhängig von der Lizenz (Users/{worldId}/hasSeoAnalyzer): ohne Lizenz
    // bleibt das Tool gesperrt, mit Lizenz aber ohne Key nur deaktiviert.
    // Gelesen wird der persistente, client-lesbare Flag hasApiKey — kein
    // Backend-Call pro Render. Wurde er nie initialisiert (Key von vor dem
    // Flag), heilt er sich genau einmal über die Status-Callable.
    const settingsRef = ref(database, `World/${userId}/settings/seoAnalyzerSettings`);
    let reconciled = false;

    const unsubKey = onValue(settingsRef, (snapshot) => {
      const v = snapshot.val() || {};

      if (typeof v.hasApiKey === 'boolean') {
        setSeoKeyReady(v.hasApiKey === true);
        return;
      }

      setSeoKeyReady(false);
      if (reconciled) return;
      reconciled = true;
      httpsCallable(functions, 'getSeoAnalyzerApiKeyStatus')()
        .then((res) => {
          const hasKey = res.data?.hasKey === true;
          const preview = res.data?.apiKeyPreview || null;
          update(ref(database, `World/${userId}/settings/seoAnalyzerSettings`), {
            hasApiKey: hasKey,
            apiKeyPreview: preview,
          }).catch(() => {});
        })
        .catch(() => {});
    });

    return () => { unsubKey(); };
  }, [userId]);

  // Load Warmup Flows from Firebase
  useEffect(() => {
    if (!userId) return;
    
    const flowsRef = ref(database, `World/${userId}/settings/warmupFlows`);
    const unsubscribe = onValue(flowsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const loadedFlows = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        // Only keep active flows
        setWarmupFlows(loadedFlows.filter(f => f.isActive));
      } else {
        setWarmupFlows([]);
      }
    });

    return () => unsubscribe();
  }, [userId]);

  // Load outreach jobs feature flag + job definitions
  useEffect(() => {
    if (!userId) return;
    const flagRef = ref(database, `Users/${userId}/hasOutreachJobs`);
    const unsub = onValue(flagRef, (snap) => setHasOutreachJobs(snap.val() === true));
    return () => unsub();
  }, [userId]);

  useEffect(() => {
    if (!userId || !hasOutreachJobs) { setOutreachJobs({}); return; }
    const jobsRef = ref(database, `World/${userId}/settings/outreachJobs`);
    const unsub = onValue(jobsRef, (snap) => {
      setOutreachJobs(snap.exists() ? snap.val() : {});
    });
    return () => unsub();
  }, [userId, hasOutreachJobs]);

  // Load user's custom flow sort order
  useEffect(() => {
    if (!userId) return;
    const sortOrderRef = ref(database, `World/${userId}/settings/flowSortOrder`);
    const ALL_CATEGORIES = ['email_ready', 'other_task', 'no_flow', 'delay', 'ended', 'disqualified'];
    const unsub = onValue(sortOrderRef, (snap) => {
      if (snap.exists()) {
        const val = snap.val();
        if (Array.isArray(val)) {
          const missing = ALL_CATEGORIES.filter(id => !val.includes(id));
          setFlowSortOrder(missing.length > 0 ? [...val.filter(id => ALL_CATEGORIES.includes(id)), ...missing] : val);
        }
      }
    });
    return () => unsub();
  }, [userId]);

  // Load only user-created (custom) templates for per-contact override dropdown
  useEffect(() => {
    if (!userId) return;

    const customRef = ref(database, `World/${userId}/settings/customTopics`);
    const unsub = onValue(customRef, (snap) => {
      if (snap.exists()) {
        const data = snap.val();
        const list = Object.entries(data).map(([id, t]) => ({
          id,
          topic: t.topic || id,
          type: t.type || 'intro'
        }));
        setEmailTemplates(list);
      } else {
        setEmailTemplates([]);
      }
    });

    return () => unsub();
  }, [userId]);

  useEffect(() => {
    // Auf die Flow-Sort-Entscheidung warten, damit der erste Fetch bereits mit
    // der finalen Sortierung läuft (sonst lädt die Liste zweimal in zwei
    // verschiedenen Reihenfolgen und Kontakte springen sichtbar herum).
    if (!flowSortDecided) return;
    itemsToLoadRef.current = PAGE_SIZE;
    autoLoadAttemptRef.current = 0;
    // WICHTIG: Suchbegriff als Parameter übergeben um Stale Closure zu vermeiden!
    fetchData(true, debouncedSearchTerm);
    // Suche abgeschlossen wenn debouncedSearchTerm sich ändert
    setIsSearching(false);
  }, [userId, sortField, sortDirection, statusFilter, importFilter, flowFilter, flowStepFilter, advancedFilters, debouncedSearchTerm, refetchTrigger, flowSortDecided]);

  useEffect(() => {
    // Beim ersten Mount läuft der Initial-Load über den gegateten Effekt oben.
    // Hier nur bei echten Reload-Triggern nachladen – und auch erst, wenn die
    // Flow-Sort-Entscheidung steht, damit keine zweite Reihenfolge dazwischenfunkt.
    if (!flowSortDecided) return;
    fetchData(true);
    // flowSortDecided bewusst NICHT in den Deps: Dieser Effekt soll nur auf
    // reloadFlag reagieren, nicht zusätzlich beim Umschalten der Entscheidung.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadFlag]);

  // View-Einstellungen cachen (30-Min-TTL), damit Sortierung/Filter/Suche einen
  // Refresh oder Tab-Wechsel überstehen. Speichert NUR die Auswahl, nicht die Daten.
  // Der localStorage-Write läuft gedrosselt im Idle/Timeout, damit er beim
  // schnellen Filter-Wechsel niemals den Klick oder das Tippen blockiert.
  useEffect(() => {
    const writePrefs = () =>
      persistTablePrefs({
        sortField,
        sortDirection,
        statusFilter,
        importFilter,
        flowFilter,
        flowStepFilter,
        advancedFilters,
        showAdvancedFilters,
        segmentFilter,
        searchTerm,
      });

    const ric = typeof window !== 'undefined' && window.requestIdleCallback;
    const cic = typeof window !== 'undefined' && window.cancelIdleCallback;
    let idleId = null;
    const timeoutId = setTimeout(() => {
      if (ric) idleId = window.requestIdleCallback(writePrefs, { timeout: 1000 });
      else writePrefs();
    }, 350);

    return () => {
      clearTimeout(timeoutId);
      if (idleId != null && cic) window.cancelIdleCallback(idleId);
    };
  }, [
    persistTablePrefs,
    sortField,
    sortDirection,
    statusFilter,
    importFilter,
    flowFilter,
    flowStepFilter,
    advancedFilters,
    showAdvancedFilters,
    segmentFilter,
    searchTerm,
  ]);

  // ── §FETCH — Main data loading from Firestore (with filters, search, pagination) ──

  const fetchData = async (isInitial = true, searchTermParam = null) => {
    if (!userId) return;
    
    // Fallback auf aktuellen debouncedSearchTerm wenn kein Parameter übergeben
    const activeSearchTerm = searchTermParam !== null ? searchTermParam : debouncedSearchTerm;

    if (isInitial) {
      setLoading(true);
      setLastDoc(null);
      setHasMoreData(true);
      setNoFilterResults(false);
    } else {
      setLoadingMore(true);
    }

    try {
      const dbRef = collection(firestore, `World/${userId}/db`);
      let currentQuery;

      let baseQuery = query(dbRef);

      // Filter functionality - ZUERST anwenden, damit sie auch bei der Suche greifen
      if (statusFilter !== 'all') {
        if (statusFilter === 'favorites') {
            baseQuery = query(baseQuery, where('tags.favorite.id', '==', 'favorite'));
        } else if (statusFilter === 'Not Contacted') {
            baseQuery = query(baseQuery, where('lead_contacted', 'in', [null, '', 'Not Contacted']));
        } else if (statusFilter === 'ready_to_spy') {
            baseQuery = query(baseQuery, where('is_ready_for_spy', '==', true));
        } else if (statusFilter === 'prepared_email') {
            // Contacts whose flow "Send Email" step has been pre-generated in the
            // backend (has_prepared_email flag, kept in sync by the prepare
            // pipeline + onDelete trigger). Ready to review + send in Bulk mode.
            baseQuery = query(baseQuery, where('has_prepared_email', '==', true));
        } else if (statusFilter === 'my_tasks') {
            baseQuery = query(baseQuery, where('open_task_assignees', 'array-contains', auth.currentUser?.uid || '__none__'));
        } else if (statusFilter === 'replied') {
            // Eigener "Replied"-State: alle Kontakte, die geantwortet haben.
            // Diese werden gleichzeitig aus den Follow-up-Stufen ausgeblendet
            // (siehe client-seitige Filterung im displayRows-useMemo), damit
            // wir nicht versehentlich ein Follow-up an Geantwortete schicken.
            baseQuery = query(baseQuery, where('lead_reply', '==', true));
        } else {
            baseQuery = query(baseQuery, where('lead_contacted', '==', statusFilter));
        }
      }
      if (importFilter !== 'all') {
        baseQuery = query(baseQuery, where('import_name', '==', importFilter));
      }

      // Advanced Filters
      if (advancedFilters.city) {
        baseQuery = query(baseQuery, where('lead_city', '==', advancedFilters.city));
      }
      if (advancedFilters.has_email !== 'any') {
        baseQuery = query(baseQuery, where('has_email', '==', advancedFilters.has_email === 'true'));
      }
      if (advancedFilters.has_website !== 'any') {
        baseQuery = query(baseQuery, where('has_website', '==', advancedFilters.has_website === 'true'));
      }
      if (advancedFilters.has_instagram !== 'any') {
        baseQuery = query(baseQuery, where('has_instagram', '==', advancedFilters.has_instagram === 'true'));
      }
      // Bounce Reduction: surface contacts where the address was found invalid
      // at send time (rerouted to a standard mailbox or parked in SafeGuard).
      if (advancedFilters.bounce_reduction && advancedFilters.bounce_reduction !== 'any') {
        if (advancedFilters.bounce_reduction === 'triggered') {
          baseQuery = query(baseQuery, where('has_bounce_check', '==', true));
        } else {
          baseQuery = query(baseQuery, where('bounce_check_action', '==', advancedFilters.bounce_reduction));
        }
      }
      // Range filter - can only apply one at a time.
      if (advancedFilters.range_field) {
        if (advancedFilters.range_min) {
          baseQuery = query(baseQuery, where(advancedFilters.range_field, '>=', Number(advancedFilters.range_min)));
        }
        if (advancedFilters.range_max) {
          baseQuery = query(baseQuery, where(advancedFilters.range_field, '<=', Number(advancedFilters.range_max)));
        }
      }

      // ============================================================
      // TRIGRAM-BASIERTE SUCHE für echte Teilstring-Suche
      // ============================================================
      // Beispiel: Suche nach "360" findet "Wassermann360 GmbH"
      // Funktioniert durch Trigram-Index (3-Zeichen-Kombinationen)
      
      // Verwende activeSearchTerm (Stale Closure Fix!)
      const effectiveSearchTerm = activeSearchTerm;
      let needsClientSideFiltering = false;
      let usesArrayContains = false;

      if (effectiveSearchTerm) {
        const normalizedSearchTerm = effectiveSearchTerm.toLowerCase().trim();

        if (normalizedSearchTerm.includes('@')) {
          baseQuery = query(
            baseQuery,
            where('lead_email', '>=', normalizedSearchTerm),
            where('lead_email', '<=', normalizedSearchTerm + '\uf8ff')
          );
        } else {
          const cleanSearchTerm = normalizedSearchTerm.replace(/[^a-z0-9äöüß\s]/gi, '');

          if (cleanSearchTerm.length >= 3) {
            const searchTrigrams = generateTrigrams(cleanSearchTerm);

            if (searchTrigrams.length > 0) {
              baseQuery = query(baseQuery, where('search_terms', 'array-contains', searchTrigrams[0]));
              usesArrayContains = true;
              needsClientSideFiltering = true;
            }
          } else {
            // < 3 chars: no useful trigram — client-side filtering only
            needsClientSideFiltering = true;
          }
        }
      }

      // Flow filter — applied server-side so pagination AND counts cover the
      // whole database, not just the currently loaded page. Snapshot the query
      // without the flow filter first, so we can gracefully fall back to it if
      // the flow-filtered query hits a not-yet-built composite index.
      const baseQueryNoFlow = baseQuery;
      const flowConstraints = buildFlowConstraints(flowFilter, flowStepFilter);
      const hasFlowFilter = flowConstraints.length > 0;
      for (const constraint of flowConstraints) {
        baseQuery = query(baseQuery, constraint);
      }

      let effectiveSortField = sortField;
      let effectiveSortDirection = sortDirection;

      const hasRangeFilter = advancedFilters.range_field && (advancedFilters.range_min || advancedFilters.range_max);
      // When a flow filter is active the flow-priority grouping is redundant
      // (rows are already narrowed to one priority/flow) and would only widen the
      // required composite index, so skip it.
      const useFlowSort = flowSortReadyRef.current && !hasRangeFilter && !hasFlowFilter;

      let usedFlowSort = false;

      if (effectiveSearchTerm) {
        if (effectiveSearchTerm.toLowerCase().includes('@')) {
          effectiveSortField = 'lead_email';
        } else {
          effectiveSortField = 'business_name_normalized';
        }
        effectiveSortDirection = 'asc';
        currentQuery = query(baseQuery, orderBy(effectiveSortField, effectiveSortDirection));
      } else if (sortField.startsWith('cf_') || CLIENT_SORT_FIELDS.has(sortField)) {
        currentQuery = query(baseQuery, orderBy('timestamp', 'desc'));
      } else {
        effectiveSortField = sortField === 'business_name' ? 'business_name_normalized'
          : sortField;
        if (sortField === 'timestamp' && useFlowSort) {
          currentQuery = query(baseQuery, orderBy('flow_sort_priority', 'asc'), orderBy(effectiveSortField, effectiveSortDirection));
          usedFlowSort = true;
        } else {
          currentQuery = query(baseQuery, orderBy(effectiveSortField, effectiveSortDirection));
        }
      }

      let loadLimit = itemsToLoadRef.current;

      if (effectiveSearchTerm && !effectiveSearchTerm.includes('@')) {
        if (usesArrayContains) {
          loadLimit = 500;
        } else if (needsClientSideFiltering) {
          loadLimit = Math.min(itemsToLoadRef.current * 3, 500);
        }
      }

      // Client-side sorted fields need a larger initial batch so the sort is meaningful
      if (!effectiveSearchTerm && (sortField.startsWith('cf_') || CLIENT_SORT_FIELDS.has(sortField))) {
        loadLimit = Math.max(loadLimit, 500);
      }

      if (isInitial) {
        currentQuery = query(currentQuery, limit(loadLimit));
      } else {
        if (!lastDoc) {
          setLoadingMore(false);
          return;
        }
        currentQuery = query(currentQuery, startAfter(lastDoc), limit(PAGE_SIZE));
      }

      let documentSnapshots;
      try {
        documentSnapshots = await getDocs(currentQuery);
      } catch (queryError) {
        if (hasFlowFilter) {
          // Flow-filtered query likely needs a composite index that isn't built
          // yet. Degrade gracefully to the non-flow query — the client-side flow
          // filter in displayRows still narrows the visible rows (as before this
          // optimization), so nothing breaks while the index builds.
          console.warn('⚠️ [FETCH] Flow-filter query failed (likely missing composite index), retrying without server-side flow filter:', queryError.message);
          const isClientSort = sortField.startsWith('cf_') || CLIENT_SORT_FIELDS.has(sortField);
          const fbField = isClientSort ? 'timestamp' : effectiveSortField;
          const fbDir = isClientSort ? 'desc' : effectiveSortDirection;
          let fallbackQuery = query(baseQueryNoFlow, orderBy(fbField, fbDir));
          if (isInitial) {
            fallbackQuery = query(fallbackQuery, limit(loadLimit));
          } else {
            if (!lastDoc) { setLoadingMore(false); return; }
            fallbackQuery = query(fallbackQuery, startAfter(lastDoc), limit(PAGE_SIZE));
          }
          try {
            documentSnapshots = await getDocs(fallbackQuery);
          } catch (fallbackErr) {
            console.warn('⚠️ [FETCH] Flow-filter fallback with orderBy failed too, retrying without orderBy:', fallbackErr.message);
            let bare = isInitial
              ? query(baseQueryNoFlow, limit(loadLimit))
              : (lastDoc ? query(baseQueryNoFlow, startAfter(lastDoc), limit(PAGE_SIZE)) : null);
            if (!bare) { setLoadingMore(false); return; }
            documentSnapshots = await getDocs(bare);
          }
        } else if (usedFlowSort) {
          console.warn('⚠️ [FETCH] Flow-sort query failed (likely missing composite index), retrying without flow_sort_priority:', queryError.message);
          let fallbackQuery = query(baseQuery, orderBy(effectiveSortField, effectiveSortDirection));
          if (isInitial) {
            fallbackQuery = query(fallbackQuery, limit(loadLimit));
          } else {
            if (!lastDoc) { setLoadingMore(false); return; }
            fallbackQuery = query(fallbackQuery, startAfter(lastDoc), limit(PAGE_SIZE));
          }
          try {
            documentSnapshots = await getDocs(fallbackQuery);
          } catch (fallbackErr) {
            // Letzter Ausweg: ohne orderBy (eine einzelne Equality-Query
            // braucht keinen Composite-Index). Sortierung übernimmt dann
            // der client-seitige displayRows-useMemo.
            console.warn('⚠️ [FETCH] Fallback-sort query failed too, retrying without orderBy:', fallbackErr.message);
            let bare = isInitial
              ? query(baseQuery, limit(loadLimit))
              : (lastDoc ? query(baseQuery, startAfter(lastDoc), limit(PAGE_SIZE)) : null);
            if (!bare) { setLoadingMore(false); return; }
            documentSnapshots = await getDocs(bare);
          }
        } else if (statusFilter === 'replied' || statusFilter === 'prepared_email' || statusFilter === 'my_tasks') {
          // Der "Replied"- und "Prepared"-Filter (where <flag> == true) brauchen
          // einen Composite-Index sobald orderBy aktiv ist. Solange der Index
          // noch nicht angelegt wurde, fallen wir auf eine orderBy-freie Query
          // zurück, statt eine leere Liste zu zeigen. Sortierung übernimmt
          // dann der client-seitige displayRows-useMemo.
          console.warn('⚠️ [FETCH] Flag-filter query failed (likely missing index), retrying without orderBy:', queryError.message);
          let bare = isInitial
            ? query(baseQuery, limit(loadLimit))
            : (lastDoc ? query(baseQuery, startAfter(lastDoc), limit(PAGE_SIZE)) : null);
          if (!bare) { setLoadingMore(false); return; }
          documentSnapshots = await getDocs(bare);
        } else {
          throw queryError;
        }
      }

      let newData = documentSnapshots.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      // ============================================================
      // CLIENT-SEITIGE FILTERUNG für präzise Teilstring-Suche
      // ============================================================
      // Diese Filterung wird IMMER bei Text-Suche angewendet,
      // um sicherzustellen, dass der komplette Suchbegriff gefunden wird.
      // Beispiel: Suche "360" muss in "Wassermann360 GmbH" gefunden werden
      
      if (effectiveSearchTerm && needsClientSideFiltering && !effectiveSearchTerm.includes('@')) {
        const normalizedSearchTerm = effectiveSearchTerm.toLowerCase().trim();
        // Entferne NUR Leerzeichen am Anfang/Ende, behalte Zahlen/Buchstaben
        const cleanSearchTerm = normalizedSearchTerm.replace(/\s+/g, ' ').trim();
        
        if (cleanSearchTerm.length > 0) {
          newData = newData.filter(item => {
            // Baue einen durchsuchbaren String aus allen relevanten Feldern
            // Extract decision maker names for search
            let dmSearchNames = '';
            try {
              const dms = item.lead_decision_makers ? (typeof item.lead_decision_makers === 'string' ? JSON.parse(item.lead_decision_makers) : item.lead_decision_makers) : [];
              dmSearchNames = dms.map(dm => dm.name).filter(Boolean).join(' ');
            } catch (e) {}

            const customFieldValues = customFields
              .map(cf => { const v = item[cf.key]; return (v && typeof v === 'string') ? v : ''; })
              .filter(Boolean);

            const allSearchableText = [
              item.business_name,
              item.lead_description,
              dmSearchNames,
              item.lead_firstname,
              item.lead_lastname,
              item.lead_city,
              item.lead_email,
              item.business_name_normalized,
              ...customFieldValues
            ].filter(Boolean).join(' ').toLowerCase();
            
            // TEILSTRING-SUCHE: Prüfe ob der Suchbegriff irgendwo vorkommt
            // Das funktioniert für "360" in "Wassermann360 GmbH"
            if (cleanSearchTerm.includes(' ')) {
              // Multi-Wort-Suche: Alle Wörter müssen vorkommen
              const searchWords = cleanSearchTerm.split(' ').filter(w => w.length > 0);
              return searchWords.every(word => allSearchableText.includes(word));
            } else {
              // Einzelwort/Teilstring-Suche: Direkter includes-Check
              return allSearchableText.includes(cleanSearchTerm);
            }
          });
        }
        
        // Bei Suche: Alle Ergebnisse anzeigen (max 500 für Performance)
        // Ohne Suche: Normales Pagination-Limit
        const resultLimit = effectiveSearchTerm ? 500 : itemsToLoadRef.current;
        newData = newData.slice(0, resultLimit);
      }

      if (isInitial && newData.length === 0) {
        setNoFilterResults(true);
      }

      setDbData(prevData => isInitial ? newData : [...prevData, ...newData]);

      const newLastDoc = documentSnapshots.docs[documentSnapshots.docs.length - 1];
      setLastDoc(newLastDoc);

      if (documentSnapshots.docs.length < PAGE_SIZE) {
        setHasMoreData(false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (isInitial) {
        setDbData([]);
        setNoFilterResults(true);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const onReload = () => {
    itemsToLoadRef.current = dbData.length > 0 ? dbData.length : PAGE_SIZE;
    fetchData(true);
    if(parentOnReload) parentOnReload();
  }

  // ============================================================
  // SEARCH MIGRATION: Prüfen ob Einträge ohne search_terms existieren
  // ============================================================
  useEffect(() => {
    const checkMigrationNeeded = async () => {
      if (!userId) return;
      
      try {
        const dbRef = collection(firestore, `World/${userId}/db`);
        const sampleQuery = query(dbRef, limit(10));
        const snapshot = await getDocs(sampleQuery);
        
        if (snapshot.empty) {
          setNeedsMigration(false);
          flowSortReadyRef.current = true;
          setFlowSortDecided(true);
          return;
        }
        
        // Check search_terms migration (shows "Enable Search" button)
        const needsSearchMigration = snapshot.docs.some(doc => {
          const data = doc.data();
          return !data.search_terms || !Array.isArray(data.search_terms) || data.search_terms.length === 0;
        });
        setNeedsMigration(needsSearchMigration);

        // Check flow_sort_priority migration (runs silently in background)
        const needsFlowSortMigration = snapshot.docs.some(doc => {
          const data = doc.data();
          return data.flow_sort_priority === undefined || data.flow_sort_priority === null;
        });
        
        if (!needsFlowSortMigration) {
          // Normalfall (bereits migriert): Flow-Sort sofort aktivieren und den
          // (gegateten) Initial-Fetch freigeben. Es gibt nur EINEN Fetch, direkt
          // mit der korrekten flow_sort_priority-Reihenfolge.
          flowSortReadyRef.current = true;
          setFlowSortDecided(true);
        } else {
          // Echte Migration nötig (einmalig): Erst ohne Flow-Sort laden, damit
          // schon Daten erscheinen, dann Backfill im Hintergrund. Nach dem
          // Backfill löst runFlowSortBackfill() den einmaligen Refetch mit
          // Flow-Sort aus.
          setFlowSortDecided(true);
          runFlowSortBackfill();
        }
      } catch (error) {
        console.error("Error checking migration:", error);
        // Fallback: Liste trotzdem laden (ohne Flow-Sort), statt leer zu bleiben.
        setFlowSortDecided(true);
      }
    };
    
    checkMigrationNeeded();
  }, [userId]);

  // Silent background backfill for flow_sort_priority + lead_contacted
  const runFlowSortBackfill = useCallback(async () => {
    if (!userId) return;
    try {
      const dbRef = collection(firestore, `World/${userId}/db`);
      const PAGE = 500;
      let lastPageDoc = null;

      while (true) {
        let pageQuery = lastPageDoc
          ? query(dbRef, orderBy('__name__'), startAfter(lastPageDoc), limit(PAGE))
          : query(dbRef, orderBy('__name__'), limit(PAGE));

        const pageSnapshot = await getDocs(pageQuery);
        if (pageSnapshot.empty) break;

        const docsToUpdate = pageSnapshot.docs.filter(d => {
          const data = d.data();
          return (data.flow_sort_priority === undefined || data.flow_sort_priority === null) || !data.lead_contacted;
        });

        if (docsToUpdate.length > 0) {
          const batch = writeBatch(firestore);
          docsToUpdate.forEach(d => {
            const data = d.data();
            const updates = {};
            if (data.flow_sort_priority === undefined || data.flow_sort_priority === null) {
              if (data.flowEndStatus || data.isDisqualified) {
                updates.flow_sort_priority = 4;
              } else if (data.warmupFlow && data.warmupFlow.flowId) {
                updates.flow_sort_priority = 2;
              } else if (data.tags && Object.keys(data.tags).some(k => k.startsWith('flow_completed_'))) {
                updates.flow_sort_priority = 4;
              } else {
                updates.flow_sort_priority = 3;
              }
            }
            if (!data.lead_contacted) {
              updates.lead_contacted = 'Not Contacted';
            }
            batch.update(d.ref, updates);
          });
          await batch.commit();
        }

        lastPageDoc = pageSnapshot.docs[pageSnapshot.docs.length - 1];
        if (pageSnapshot.size < PAGE) break;
      }

      flowSortReadyRef.current = true;
      setRefetchTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Flow sort backfill error:", error);
    }
  }, [userId]);
  
  const handleMigration = useCallback(async () => {
    if (!userId || isMigrating) return;

    setIsMigrating(true);
    setMigrationProgress(0);

    try {
      const dbRef = collection(firestore, `World/${userId}/db`);
      const PAGE = 500;
      let lastPageDoc = null;
      let totalProcessed = 0;
      let totalToMigrate = 0;
      let firstPass = true;

      // Paginate through the collection instead of loading everything at once
      while (true) {
        let pageQuery = lastPageDoc
          ? query(dbRef, orderBy('__name__'), startAfter(lastPageDoc), limit(PAGE))
          : query(dbRef, orderBy('__name__'), limit(PAGE));

        const pageSnapshot = await getDocs(pageQuery);
        if (pageSnapshot.empty) break;

        const docsNeedingUpdate = pageSnapshot.docs.filter(d => {
          const data = d.data();
          const missingSearch = !data.search_terms || !Array.isArray(data.search_terms) || data.search_terms.length === 0;
          const missingFlowPriority = data.flow_sort_priority === undefined || data.flow_sort_priority === null;
          return missingSearch || missingFlowPriority;
        });

        if (firstPass) {
          const ratio = docsNeedingUpdate.length / pageSnapshot.size;
          const countSnap = await getCountFromServer(query(dbRef));
          totalToMigrate = Math.max(1, Math.round(countSnap.data().count * ratio));
          firstPass = false;
        }

        if (docsNeedingUpdate.length > 0) {
          const batch = writeBatch(firestore);
          docsNeedingUpdate.forEach(d => {
            const data = d.data();
            const updates = {};
            if (!data.search_terms || !Array.isArray(data.search_terms) || data.search_terms.length === 0) {
              updates.search_terms = buildSearchTerms(data);
            }
            if (data.flow_sort_priority === undefined || data.flow_sort_priority === null) {
              if (data.flowEndStatus || data.isDisqualified) {
                updates.flow_sort_priority = 4;
              } else if (data.warmupFlow && data.warmupFlow.flowId) {
                updates.flow_sort_priority = 2;
              } else if (data.tags && Object.keys(data.tags).some(k => k.startsWith('flow_completed_'))) {
                updates.flow_sort_priority = 4;
              } else {
                updates.flow_sort_priority = 3;
              }
            }
            if (!data.lead_contacted) {
              updates.lead_contacted = 'Not Contacted';
            }
            batch.update(d.ref, updates);
          });
          await batch.commit();
          totalProcessed += docsNeedingUpdate.length;
          setMigrationProgress(Math.min(99, Math.round((totalProcessed / totalToMigrate) * 100)));
        }

        lastPageDoc = pageSnapshot.docs[pageSnapshot.docs.length - 1];
        if (pageSnapshot.size < PAGE) break;
      }

      setMigrationProgress(100);
      setNeedsMigration(false);
      flowSortReadyRef.current = true;
      setRefetchTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Migration error:", error);
      alert("Migration failed. Please try again.");
    } finally {
      setIsMigrating(false);
    }
  }, [userId, isMigrating]);

  useEffect(() => {
    const getFilteredCount = async () => {
      if (!userId) return;

      const isFilterActive = statusFilter !== 'all' || importFilter !== 'all' || (flowFilter && flowFilter !== 'all') || Object.values(advancedFilters).some(value => value !== 'any' && value !== '');
      if (!isFilterActive) {
        setFilteredCount(null);
        return;
      }

      const dbRef = collection(firestore, `World/${userId}/db`);
      let countQuery = query(dbRef);

      if (statusFilter !== 'all') {
        if (statusFilter === 'favorites') {
          countQuery = query(countQuery, where('tags.favorite.id', '==', 'favorite'));
        } else if (statusFilter === 'Not Contacted') {
          countQuery = query(countQuery, where('lead_contacted', 'in', [null, '', 'Not Contacted']));
        } else if (statusFilter === 'ready_to_spy') {
          countQuery = query(countQuery, where('is_ready_for_spy', '==', true));
        } else if (statusFilter === 'prepared_email') {
          countQuery = query(countQuery, where('has_prepared_email', '==', true));
        } else if (statusFilter === 'my_tasks') {
          countQuery = query(countQuery, where('open_task_assignees', 'array-contains', auth.currentUser?.uid || '__none__'));
        } else if (statusFilter === 'replied') {
          countQuery = query(countQuery, where('lead_reply', '==', true));
        } else {
          countQuery = query(countQuery, where('lead_contacted', '==', statusFilter));
        }
      }
      if (importFilter !== 'all') {
        countQuery = query(countQuery, where('import_name', '==', importFilter));
      }

      // Add advanced filters to count query
      if (advancedFilters.city) {
        countQuery = query(countQuery, where('lead_city', '==', advancedFilters.city));
      }
      if (advancedFilters.has_email !== 'any') {
        countQuery = query(countQuery, where('has_email', '==', advancedFilters.has_email === 'true'));
      }
      if (advancedFilters.has_website !== 'any') {
        countQuery = query(countQuery, where('has_website', '==', advancedFilters.has_website === 'true'));
      }
      if (advancedFilters.has_instagram !== 'any') {
        countQuery = query(countQuery, where('has_instagram', '==', advancedFilters.has_instagram === 'true'));
      }
      if (advancedFilters.bounce_reduction && advancedFilters.bounce_reduction !== 'any') {
        if (advancedFilters.bounce_reduction === 'triggered') {
          countQuery = query(countQuery, where('has_bounce_check', '==', true));
        } else {
          countQuery = query(countQuery, where('bounce_check_action', '==', advancedFilters.bounce_reduction));
        }
      }
      if (advancedFilters.range_min) {
        countQuery = query(countQuery, where(advancedFilters.range_field, '>=', Number(advancedFilters.range_min)));
      }
      if (advancedFilters.range_max) {
        countQuery = query(countQuery, where(advancedFilters.range_field, '<=', Number(advancedFilters.range_max)));
      }

      // Flow filter — same server-side constraints as the main query so the
      // "X of Y" count reflects the flow filter too.
      const hasFlowFilter = flowFilter && flowFilter !== 'all';
      for (const constraint of buildFlowConstraints(flowFilter, flowStepFilter)) {
        countQuery = query(countQuery, constraint);
      }

      try {
        const snapshot = await getCountFromServer(countQuery);
        setFilteredCount(snapshot.data().count);
      } catch (error) {
        // A flow-filtered count may need a not-yet-built composite index. Rather
        // than showing a misleading "0", fall back to null so the UI shows the
        // total database count until the index is ready.
        console.error("Error getting filtered count:", error);
        setFilteredCount(hasFlowFilter ? null : 0);
      }
    };

    getFilteredCount();
  }, [userId, statusFilter, importFilter, flowFilter, flowStepFilter, advancedFilters, reloadFlag]);

  // Live count of pre-prepared flow emails for the nav quick-action button.
  useEffect(() => {
    let cancelled = false;
    const fetchPreparedCount = async () => {
      if (!userId) return;
      try {
        const dbRef = collection(firestore, `World/${userId}/db`);
        const snap = await getCountFromServer(query(dbRef, where('has_prepared_email', '==', true)));
        if (!cancelled) setPreparedCount(snap.data().count || 0);
      } catch (error) {
        // A missing single-field index shouldn't happen (auto-indexed), but stay
        // silent and keep the button hidden rather than break the nav.
        if (!cancelled) setPreparedCount(0);
      }
    };
    fetchPreparedCount();
    return () => { cancelled = true; };
  }, [userId, reloadFlag, preparedCountTrigger]);

  useEffect(() => {
    const fetchAllImportOptions = async () => {
      if (!userId) return;
      try {
        const importNamesRef = collection(firestore, `World/${userId}/import_names`);
        const q = query(importNamesRef, orderBy('name', 'asc'));
        const documentSnapshots = await getDocs(q);
        
        const imports = documentSnapshots.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            name: data.name,
            last_imported: data.last_imported || null,
            imported_count: data.imported_count ?? data.total_count ?? null,
            template_name: data.template_name || null,
            status: data.status || null,
          };
        });
        
        setImportOptions(imports);

        const dbRef = collection(firestore, `World/${userId}/db`);
        const updated = [...imports];
        await Promise.all(imports.map(async (item) => {
          try {
            const countQ = query(dbRef, where('import_name', '==', item.name));
            const snap = await getCountFromServer(countQ);
            const count = snap.data().count;
            const idx = updated.findIndex(i => i.name === item.name);
            if (idx >= 0) updated[idx] = { ...updated[idx], imported_count: count };
            const metaRef = doc(firestore, `World/${userId}/import_names`, item.id);
            setDoc(metaRef, { imported_count: count }, { merge: true }).catch(() => {});
          } catch { /* skip */ }
        }));
        setImportOptions(updated);
      } catch (error) {
          console.error("Error fetching all import options:", error);
      }
    };
    
    fetchAllImportOptions();
  }, [userId]);

  const emailPopupShowRef = useRef(emailPopupData.show);
  emailPopupShowRef.current = emailPopupData.show;

  const saveScrollPosition = useCallback(() => {
    if (emailPopupShowRef.current || scrollLockedRef.current) return;
    if (tableContainerRef.current) {
      scrollPositionRef.current = {
        top: tableContainerRef.current.scrollTop,
        left: tableContainerRef.current.scrollLeft,
      };
    }
  }, []);

  const restoreScrollPosition = useCallback(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = scrollPositionRef.current.top;
      tableContainerRef.current.scrollLeft = scrollPositionRef.current.left;
      scrollLockedRef.current = false;
    }
  }, []);

  const handleWheel = (e) => {
    if (!isEditing) {
      saveScrollPosition();
    }
    // Block horizontal overscroll at table boundaries to prevent
    // browser back/forward navigation on macOS trackpads.
    // Skip if the event comes from inside the email popup — its own
    // scroll containers handle horizontal overflow independently.
    if (e.target.closest('.emailAiPopupOverlay')) return;
    const container = tableContainerRef.current;
    if (!container) return;
    const dx = Math.abs(e.deltaX);
    const dy = Math.abs(e.deltaY);
    if (dx > dy) {
      const atLeft = container.scrollLeft <= 0;
      const atRight = container.scrollLeft + container.clientWidth >= container.scrollWidth - 1;
      if ((atLeft && e.deltaX < 0) || (atRight && e.deltaX > 0)) {
        e.preventDefault();
      }
    }
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, [isEditing]);

  const selectedCount = Object.values(selectedRows).filter(Boolean).length;

  const handleSortChange = (field, direction) => {
    saveAllPendingEdits();
    saveScrollPosition();
    setSortField(field);
    setSortDirection(direction);
  };
  
  const renderSortButtons = (colKey) => {
    // Determine if sorting by this column is currently possible
    const isSortable = sortableColumns.includes(colKey);
    let isDisabled = false;
    let tooltip = '';

    if (searchTerm) {
      isDisabled = true;
      tooltip = 'Sorting is disabled during search.';
    }

    if (!isSortable) return null;

    const isActive = !searchTerm && sortField === colKey;
    return (
      <div className="sort-controls" title={tooltip}>
        <button 
          className={`sort-up-btn ${isActive && sortDirection === 'asc' ? 'active' : ''}`}
          onClick={(e) => { e.stopPropagation(); handleSortChange(colKey, 'asc'); }}
          disabled={isDisabled}
          title={isDisabled ? tooltip : "Aufsteigend sortieren"}
        >
          ▲
        </button>
        <button 
          className={`sort-down-btn ${isActive && sortDirection === 'desc' ? 'active' : ''}`}
          onClick={(e) => { e.stopPropagation(); handleSortChange(colKey, 'desc'); }}
          disabled={isDisabled}
          title={isDisabled ? tooltip : "Absteigend sortieren"}
        >
          ▼
        </button>
      </div>
    );
  };

  const orderedColumns = columnOrder;

  const saveAllPendingEdits = () => {
    Object.entries(pendingEdits).forEach(([key, value]) => {
      const [rowId, fieldKey] = key.split('-');
      if (rowId && fieldKey) {
        saveToDatabase(rowId, fieldKey, value);
      }
    });
    setPendingEdits({});
  };
  
  // Stabiler Key über die E-Mails der aktuell geladenen Kontakte. So feuert der
  // Blacklist-Check nur, wenn sich das Email-Set wirklich ändert (Pagination,
  // Reload) — nicht bei jeder unbeteiligten Inline-Bearbeitung.
  const loadedEmailsKey = useMemo(() => {
    const emails = new Set();
    dbData.forEach((row) => {
      const email = (row?.lead_email || '').toLowerCase().trim();
      if (email && email.includes('@')) emails.add(email);
    });
    return Array.from(emails).sort().join('|');
  }, [dbData]);

  // Blacklist-Abgleich, damit blockierte Kontakte (inkl. ganzer Domains) aus der
  // Tabelle gefiltert werden. Wir prüfen NUR die E-Mails der geladenen Kontakte
  // serverseitig (skaliert mit der Anzahl der Kontakte, nicht mit der Blacklist-
  // Größe). Das volle Laden der Blacklist war bei Usern mit sehr großer Blacklist
  // (zehntausende Einträge) der Grund für mehrere Sekunden Ladezeit.
  useEffect(() => {
    if (!userId) { setBlacklistSets(null); setBlacklistResolvedKey(null); return undefined; }
    if (!loadedEmailsKey) {
      setBlacklistSets({ emails: new Set(), domains: new Set() });
      setBlacklistResolvedKey('');
      return undefined;
    }
    let cancelled = false;
    const emails = loadedEmailsKey.split('|');
    lookupBlacklistForEmails(emails, userId)
      .then(sets => { if (!cancelled) { setBlacklistSets(sets); setBlacklistResolvedKey(loadedEmailsKey); } })
      .catch(err => {
        console.warn('[NewWorldDbTable] lookupBlacklistForEmails failed:', err?.message);
        // Never let a failed lookup block the reveal — treat the page as settled.
        if (!cancelled) setBlacklistResolvedKey(loadedEmailsKey);
      });
    return () => { cancelled = true; };
  }, [userId, loadedEmailsKey, reloadFlag, refetchTrigger]);

  // True once the blacklist filter has resolved for the data currently loaded,
  // so the staggered reveal can show the FINAL set in a single pass.
  const blacklistReady = blacklistSets !== null && blacklistResolvedKey === loadedEmailsKey;

  // Apple-like, flüssiges Umschalten: Die Filter-States selbst bleiben "urgent"
  // (Buttons/Selects reagieren sofort), aber die teure Neuberechnung + das
  // Neu-Rendern aller Zeilen läuft über deferred Werte in einem nicht-
  // blockierenden Transition. So ruckelt der Klick nicht mehr, während React
  // die Liste im Hintergrund aktualisiert. displayRows bleibt EINE konsistente
  // Quelle (Rendering + Klick-Handler), daher gibt es keine Index-Mismatches.
  const deferredFlowFilter = useDeferredValue(flowFilter);
  const deferredFlowStepFilter = useDeferredValue(flowStepFilter);
  const deferredSegmentFilter = useDeferredValue(segmentFilter);
  const deferredStatusFilter = useDeferredValue(statusFilter);
  const deferredSortField = useDeferredValue(sortField);
  const deferredSortDirection = useDeferredValue(sortDirection);

  // True, solange die Tabelle noch auf einen neuen Filter "nachzieht". Wird nur
  // für ein dezentes Abdimmen genutzt (bei kleinen Listen praktisch unsichtbar).
  const isFilterPending =
    deferredFlowFilter !== flowFilter ||
    deferredFlowStepFilter !== flowStepFilter ||
    deferredSegmentFilter !== segmentFilter ||
    deferredStatusFilter !== statusFilter ||
    deferredSortField !== sortField ||
    deferredSortDirection !== sortDirection;

  // Client-seitige Filterung für Suche und Flow
  const displayRows = useMemo(() => {
    // Lokale Aliase auf die deferred Werte — der restliche Body bleibt unverändert.
    const flowFilter = deferredFlowFilter;
    const flowStepFilter = deferredFlowStepFilter;
    const segmentFilter = deferredSegmentFilter;
    const statusFilter = deferredStatusFilter;
    const sortField = deferredSortField;
    const sortDirection = deferredSortDirection;

    let rows = dbData.filter(row => row !== undefined && row !== null).map((row, index) => {
      if (!row.id) return { ...row, id: String(index) };
      return row;
    });

    // Blockierte Kontakte global ausblenden — in JEDER Ansicht. Sie sollen weder
    // in den Follow-ups noch sonst irgendwo in der DB sichtbar sein, damit nie
    // versehentlich (Follow-up-)Mails an blockierte Kontakte rausgehen.
    // (Verwaltung/Aufhebung läuft über die Blacklist-Einstellungen.)
    rows = rows.filter(row => {
      if (isBlockedContact(row)) return false;
      // Zusätzlich gegen die Blacklist abgleichen — fängt ganze geblockte
      // Domains ab, auch wenn auf dem einzelnen Kontakt (noch) keine Flags sitzen.
      if (blacklistSets && row.lead_email && isEmailBlacklisted(row.lead_email, blacklistSets)) {
        return false;
      }
      // Phase E (2026-08): Gemergte Kontakte ausblenden. Der "loser" eines
      // Merge-Vorgangs bekommt `merged_into` gesetzt — Historie bleibt am
      // Winner, das Loser-Doc bleibt technisch als Audit-Spur bestehen, wird
      // aber aus der aktiven Kontaktliste ausgeblendet.
      if (row.merged_into) return false;
      return true;
    });
    
    // Flow-Filter client-seitig anwenden
    if (flowFilter && flowFilter !== 'all') {
      rows = rows.filter(row => {
        const hasFlow = row.warmupFlow && row.warmupFlow.flowId;
        
        if (flowFilter === 'no_flow') {
          return !hasFlow;
        } else if (flowFilter === 'has_flow') {
          return hasFlow;
        } else if (flowFilter === 'finished') {
          // Mirrors the "FIN" badge (WarmupFlowBadge.js): no active flow AND
          // the flow ended with the plain "completed" status.
          return !hasFlow && row.flowEndStatus === 'completed';
        } else {
          // "Flow Ended" – show contacts that completed this specific flow (no active flow)
          if (flowStepFilter === 'flow_ended') {
            const history = row.warmupFlowHistory || [];
            const tags = row.tags || {};
            const flowCompletedTagKey = `flow_completed_${flowFilter}`;
            // Match by history OR by the flow-completed tag
            return history.some(h => h.flowId === flowFilter && (h.reason === 'completed' || h.reason === 'stopped'))
              || !!tags[flowCompletedTagKey];
          }
          
          // Spezifischer Flow – active flow must match
          if (row.warmupFlow?.flowId !== flowFilter) {
            return false;
          }
          
          // Step-Filter wenn aktiv
          if (flowStepFilter && flowStepFilter !== 'all') {
            const stepIndex = parseInt(flowStepFilter, 10);
            return row.warmupFlow?.currentStepIndex === stepIndex;
          }
          
          return true;
        }
      });
    }
    
    // Segment-Filter (via ActionCenter "Fokus setzen")
    if (segmentFilter) {
      rows = rows.filter(row => {
        const s = row.leadSignals;
        if (!s) return false;
        if (segmentFilter.offerType && segmentFilter.offerType !== 'unknown' && s.offerType !== segmentFilter.offerType) return false;
        if (segmentFilter.b2b_b2c && segmentFilter.b2b_b2c !== 'unknown' && s.b2b_b2c !== segmentFilter.b2b_b2c) return false;
        if (segmentFilter.industry && segmentFilter.industry !== 'unknown' && s.industry !== segmentFilter.industry) return false;
        if (segmentFilter.sizeProxy && segmentFilter.sizeProxy !== 'unknown' && s.sizeProxy !== segmentFilter.sizeProxy) return false;
        return true;
      });
    }

    // Follow-up-Stufen: Kontakte, die bereits geantwortet haben, ausblenden,
    // damit kein versehentliches Follow-up an Geantwortete rausgeht. Sie sind
    // weiterhin über den "Replied"-Filter erreichbar.
    if (FOLLOWUP_STAGES.has(statusFilter)) {
      rows = rows.filter(row => row.lead_reply !== true);
    }

    if (statusFilter === 'my_tasks') {
      const myUid = auth.currentUser?.uid;
      rows = rows.filter(row => Array.isArray(row.open_task_assignees) && row.open_task_assignees.includes(myUid));
    }

    if (statusFilter === 'Not Contacted') {
      rows = rows.filter(row => {
        if (!row.tags) return true;
        return !STRIKETHROUGH_TAGS.some(tagId => {
          const tag = row.tags[tagId] || Object.values(row.tags).find(t =>
            t && typeof t === 'object' && (t.id === tagId || (t.name && t.name.toLowerCase().replace(/\s+/g, '_') === tagId))
          );
          if (!tag) return false;
          if (tag.addedBy === 'flow') return false;
          return true;
        });
      });
    }

    if (sortField.startsWith('cf_') || CLIENT_SORT_FIELDS.has(sortField)) {
      const cfType = customFieldTypes[sortField] || 'text';
      rows.sort((a, b) => {
        const aVal = getClientSortValue(a, sortField, cfType);
        const bVal = getClientSortValue(b, sortField, cfType);
        return clientSideCompare(aVal, bVal, sortDirection);
      });
    }

    // Flow priority grouping is handled server-side via Firestore's flow_sort_priority
    // field in the default (timestamp) query. No client-side re-sort needed — this
    // ensures explicit column sorts are never overridden.

    return rows;
  }, [dbData, deferredFlowFilter, deferredFlowStepFilter, deferredSegmentFilter, deferredStatusFilter, deferredSortField, deferredSortDirection, customFieldTypes, blacklistSets]);

  // Anzahl der angezeigten Zeilen (nach Filterung)
  const displayedCount = useMemo(() => displayRows.length, [displayRows]);

  // Progressive reveal: once the data is ready, swap the skeleton placeholders
  // for real rows top-to-bottom. The skeleton rows keep their keys across the
  // loading→loaded transition, so React reuses their DOM (no blank flash), and
  // each real row materialises in the exact spot its skeleton occupied.
  const [revealCount, setRevealCount] = useState(0);
  // One-shot guard: the staggered reveal plays exactly once per load cycle. It
  // resets only when a fresh initial fetch starts (loading flips true). This is
  // what stops the animation from "restarting mid-way" when something changes
  // displayRows.length AFTER the first reveal (the async blacklist filter
  // removing rows, a deletion, a live edit, …).
  const hasRevealedRef = useRef(false);
  const revealTimerRef = useRef(null);
  useEffect(() => {
    // Still loading the first page (no data yet) → keep all placeholders and
    // arm the reveal for this fresh cycle.
    if (loading && !loadingMore) { hasRevealedRef.current = false; setRevealCount(0); return; }
    // Appending more rows shouldn't restart the reveal of the existing ones.
    if (loadingMore) return;
    if (!displayRows.length) { setRevealCount(0); return; }

    // Already revealed this cycle → keep everything shown; never re-stagger on
    // incidental length changes.
    if (hasRevealedRef.current) { setRevealCount(displayRows.length); return; }

    const startReveal = () => {
      hasRevealedRef.current = true;
      const total = Math.min(displayRows.length, SKELETON_COUNT);
      let i = 0;
      const tick = () => {
        i += 1;
        if (i >= total) {
          // Reveal everything that's left (incl. rows below the fold) at once.
          setRevealCount(displayRows.length);
        } else {
          setRevealCount(i);
          revealTimerRef.current = setTimeout(tick, REVEAL_STEP_MS);
        }
      };
      tick();
    };

    // Wait for the blacklist filter to settle for the current page so the first
    // reveal already shows the final, filtered set — no mid-animation restart.
    // A safety timeout guarantees a slow/failed lookup can never block the list.
    let waitTimer;
    if (blacklistReady) {
      startReveal();
    } else {
      waitTimer = setTimeout(startReveal, REVEAL_SETTLE_MAX_WAIT_MS);
    }
    return () => {
      clearTimeout(waitTimer);
      clearTimeout(revealTimerRef.current);
    };
  }, [loading, loadingMore, displayRows.length, blacklistReady]);

  // Replay cached column widths (measured on a previous load) so the layout is
  // locked from the first paint — skeleton and real rows share identical widths,
  // eliminating the horizontal slide. Initialised synchronously from cache.
  const [frozenColWidths, setFrozenColWidths] = useState(() => readColWidthCache(userId));
  useEffect(() => {
    setFrozenColWidths(readColWidthCache(userId));
  }, [userId]);

  // We can lock the layout only if we have a cached width for every column that
  // is currently visible (otherwise an un-sized column would collapse/stretch).
  const canFreezeWidths = useMemo(() => {
    if (!frozenColWidths) return false;
    return orderedColumns.every((colKey) => {
      if (colKey === "selectCheckbox" && !isSelectMode) return true;
      return frozenColWidths[colKey] > 0;
    });
  }, [frozenColWidths, orderedColumns, isSelectMode]);

  // After real data is on screen, measure the natural column widths and cache
  // them for next time. While the layout is already frozen this just re-confirms
  // the same numbers; when running in auto-layout it captures the real widths.
  useEffect(() => {
    if (loading || loadingMore || !userId || !displayRows.length) return;
    const timer = setTimeout(() => {
      const table = tableRef.current;
      if (!table) return;
      const ths = table.querySelectorAll("thead th[data-column]");
      if (!ths.length) return;
      const widths = {};
      ths.forEach((th) => {
        const key = th.getAttribute("data-column");
        const w = th.getBoundingClientRect().width;
        if (key && w > 0) widths[key] = Math.round(w);
      });
      if (Object.keys(widths).length) {
        writeColWidthCache(userId, widths);
        // Adopt the freshly measured widths so this session also locks (only
        // matters on a cold first load that had no cache yet).
        setFrozenColWidths((prev) => prev || widths);
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [loading, loadingMore, userId, displayRows.length]);

  // The cache is keyed per user, not per world, so a width measured in a world
  // with few socials gets replayed in one with many. Under table-layout:fixed the
  // cell's own min-width no longer applies, so clamp the icon columns here and pin
  // the table to the total — otherwise the fixed layout scales every column down
  // on a narrow window and the icons get clipped again.
  const frozenLayout = useMemo(() => {
    if (!canFreezeWidths) return null;
    const cols = orderedColumns
      .filter((colKey) => colKey !== "selectCheckbox" || isSelectMode)
      .map((colKey) => ({
        colKey,
        width: Math.max(frozenColWidths[colKey] || 0, MIN_FROZEN_COL_WIDTHS[colKey] || 0),
      }));
    const totalWidth = cols.reduce((sum, col) => sum + col.width, 0);
    return { cols, totalWidth: Math.max(totalWidth, TABLE_MIN_WIDTH) };
  }, [canFreezeWidths, frozenColWidths, orderedColumns, isSelectMode]);

  // Auto-load more when client-side filters (e.g. STRIKETHROUGH_TAGS) eliminate all results
  useEffect(() => {
    if (loading || loadingMore) return;
    if (displayRows.length > 0 || !hasMoreData) {
      autoLoadAttemptRef.current = 0;
      return;
    }
    if (dbData.length === 0) return;
    if (autoLoadAttemptRef.current >= 5) return;

    autoLoadAttemptRef.current += 1;
    fetchData(false);
  }, [displayRows.length, dbData.length, loading, loadingMore, hasMoreData]);

  // ── §EDIT — Inline cell editing + Firestore persistence ──

  // Phase E-Fix (2026-08): Client-Duplikat-Check nach einem lead_email-Save.
  // Nutzt das gleiche Indexfeld (lead_email_dedup) wie die Import-Prüfung
  // und der Server-Trigger — konsistente Normalisierung, ein Doc-Read pro
  // Emailadresse aus dem Feld. Fail-silent: Hint ist nur additiv.
  const checkEmailDuplicateInline = useCallback(async (rowId, rawEmailField) => {
    if (!userId || !rowId || typeof rawEmailField !== 'string') return;
    const parts = rawEmailField.split(/[,;]/).map(s => s.trim()).filter(Boolean);
    if (parts.length === 0) return;
    const normalized = Array.from(new Set(
      parts.map(e => normalizeEmailForComparison(e)).filter(Boolean),
    ));
    if (normalized.length === 0) return;

    try {
      const dbRef = collection(firestore, `World/${userId}/db`);
      const CHUNK = 30;
      const allMatches = new Map(); // id → { id, businessName, email }
      for (let i = 0; i < normalized.length; i += CHUNK) {
        const batch = normalized.slice(i, i + CHUNK);
        const q = query(dbRef, where('lead_email_dedup', 'in', batch));
        const snap = await getDocs(q);
        snap.forEach(docSnap => {
          if (docSnap.id === rowId) return; // gleicher Kontakt: ignorieren
          const data = docSnap.data() || {};
          if (data.merged_into) return; // Loser eines Merges: ignorieren
          if (!allMatches.has(docSnap.id)) {
            allMatches.set(docSnap.id, {
              id: docSnap.id,
              businessName: data.business_name || '(ohne Name)',
              email: data.lead_email_dedup,
            });
          }
        });
      }

      if (allMatches.size === 0) return;
      const list = Array.from(allMatches.values());
      setDuplicateHint({
        email: list[0].email || normalized[0],
        matches: list,
        rowId,
      });
    } catch (err) {
      console.warn('[dedup-inline-hint] check skipped (non-blocking):', err && err.message);
    }
  }, [userId]);

  const saveToDatabase = useCallback((rowId, key, value) => {
    if (!userId || !rowId) {
      setIsEditing(false);
      return;
    }
    
    setPendingEdits(prev => {
      const newPending = { ...prev };
      delete newPending[`${rowId}-${key}`];
      return newPending;
    });
    
    const rowRef = doc(firestore, `World/${userId}/db/${rowId}`);
    
    const updates = { 
      [key]: value,
      lead_last_edit: {
        time: new Date().toISOString(),
        user: userId
      }
    };

    // Aktualisiere normalisierte Felder für optimale Suche
    if (key === 'business_name' || key === 'lead_description') {
      const currentRow = dbData.find(row => row.id === rowId);
      const businessName = key === 'business_name' ? value : (currentRow?.business_name || '');
      const leadDescription = key === 'lead_description' ? value : (currentRow?.lead_description || '');
      
      const namePart = typeof businessName === 'string' ? businessName.toLowerCase() : '';
      const descPart = typeof leadDescription === 'string' ? leadDescription.toLowerCase() : '';
      updates.business_name_normalized = `${namePart} ${descPart}`.trim();
      
      if (key === 'lead_description') {
        updates.lead_description_normalized = descPart;
      }
    }

    // Phase B (additiv, 2026-08): Dedup-Index-Felder synchronisieren, wenn ein
    // identitätsrelevantes Feld inline editiert wird. Verhindert Blind Spots,
    // wenn User in der Tabelle E-Mail/Website/Firmenname ändern.
    // - Fail-safe: bei irgendeinem Fehler läuft der Original-Write unverändert.
    // - Idempotent: dedupFieldPatch gibt {} zurück, wenn sich nichts ändert.
    if (key === 'lead_email' || key === 'lead_website' || key === 'business_name') {
      try {
        const currentRow = dbData.find(row => row.id === rowId) || {};
        const patch = dedupFieldPatch(currentRow, { [key]: value });
        Object.assign(updates, patch);
      } catch (err) {
        console.warn('[dedup-sync] inline-edit patch skipped (non-blocking):', err && err.message);
      }
    }

    updateDoc(rowRef, updates)
      .then(() => {
        setTimeout(() => {
          setIsEditing(false);
          restoreScrollPosition();
        }, 300);

        // Phase E-Fix: unmittelbarer Duplikat-Check nach lead_email-Edit.
        // Der Server-Trigger onContactWriteDuplicateAlert erzeugt sowieso
        // einen persistenten Alert — dieser Client-Check liefert das
        // sofortige, kontextuelle Feedback am Ort des Edits.
        if (key === 'lead_email' && typeof value === 'string' && value.trim()) {
          checkEmailDuplicateInline(rowId, value).catch(() => { /* fail-silent */ });
        }

        if (key === 'lead_description' || key === 'business_name') {
          const row = displayRows.find(r => r.id === rowId);
          if (row) {
            const freshData = { ...row, [key]: value };
            computeAndStoreSignals(userId, rowId, freshData).catch(() => {});
          }
        }

        if (key === 'lead_industry') {
          const row = displayRows.find(r => r.id === rowId);
          if (row && value) {
            const oldSignalVal = row.leadSignals?.industry || 'unknown';
            if (value !== oldSignalVal) {
              recordSignalCorrection(userId, rowId, 'industry', oldSignalVal, value, row).catch(() => {});
            }
          }
        }
      })
      .catch((error) => {
        console.error("Fehler beim Speichern:", error);
        setIsEditing(false);
        restoreScrollPosition();
      });
  }, [userId, restoreScrollPosition, dbData, displayRows, checkEmailDuplicateInline]);

  const handleEdit = useCallback((rowId, key, value) => {
    saveScrollPosition();
    setIsEditing(true);

    let processedValue = value;
    if (customFieldTypes[key] === 'number') {
      const parsed = parseFloat(String(value).replace(/[^0-9.,\-]/g, '').replace(',', '.'));
      processedValue = isNaN(parsed) ? '' : parsed;
    }

    setEditedData((prev) => ({
      ...prev,
      [`${rowId}-${key}`]: processedValue,
    }));

    setPendingEdits(prev => ({
      ...prev,
      [`${rowId}-${key}`]: processedValue
    }));

    setDbData(prevData => {
      return prevData.map(row => {
        if (row && row.id === rowId) {
          return {
            ...row,
            [key]: processedValue,
            lead_last_edit: {
              time: new Date().toISOString(),
              user: userId
            },
          };
        }
        return row;
      });
    });

    saveToDatabase(rowId, key, processedValue);
    setTimeout(restoreScrollPosition, 50);
  }, [customFieldTypes, userId, saveScrollPosition, restoreScrollPosition, saveToDatabase]);

  // Cell edits typed by a person. Research hooks call handleEdit directly and
  // are recorded as research runs instead.
  const handleUserEdit = useCallback((rowId, key, value) => {
    const before = dbData.find((r) => r && r.id === rowId) || {};
    if (String(before[key] ?? '').trim() !== String(value ?? '').trim()) {
      const entry = describeEdit({ [key]: value }, before);
      if (entry) logContactActivity(firestore, userId, rowId, entry);
    }
    handleEdit(rowId, key, value);
  }, [dbData, userId, handleEdit]);

  const handleToggleFavorite = useCallback(async (rowId) => {
    if (!userId || !rowId) return;
    const contact = dbData.find(r => r.id === rowId);
    if (!contact) return;
    const wasFav = !!(contact.tags && contact.tags[FAVORITE_TAG.id]);
    const now = Date.now();
    const contactRef = doc(firestore, `World/${userId}/db/${rowId}`);
    try {
      if (wasFav) {
        await updateDoc(contactRef, { [`tags.${FAVORITE_TAG.id}`]: deleteField(), updated_at: now });
      } else {
        await updateDoc(contactRef, {
          [`tags.${FAVORITE_TAG.id}`]: { id: FAVORITE_TAG.id, name: FAVORITE_TAG.name, color: FAVORITE_TAG.color, icon: FAVORITE_TAG.icon, addedAt: now, addedBy: 'worlddb' },
          updated_at: now,
        });
      }
      setDbData(prev => prev.map(r => {
        if (r.id !== rowId) return r;
        const tags = { ...(r.tags || {}) };
        if (wasFav) { delete tags[FAVORITE_TAG.id]; } else { tags[FAVORITE_TAG.id] = { id: FAVORITE_TAG.id, name: FAVORITE_TAG.name, color: FAVORITE_TAG.color, icon: FAVORITE_TAG.icon, addedAt: now, addedBy: 'worlddb' }; }
        return { ...r, tags };
      }));
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  }, [userId, dbData]);
  
  const loadMoreData = () => {
    if (hasMoreData && !loadingMore) {
      itemsToLoadRef.current = dbData.length + PAGE_SIZE;
      fetchData(false);
    }
  };

  const handleBulkEdit = useCallback(async (rowId, updatesObj) => {
    if (!userId || !rowId || !updatesObj) return;

    const rowRef = doc(firestore, `World/${userId}/db/${rowId}`);
    const firestoreUpdates = {
      ...updatesObj,
      lead_last_edit: { time: new Date().toISOString(), user: userId }
    };

    if (updatesObj.business_name !== undefined || updatesObj.lead_description !== undefined) {
      const currentRow = dbData.find(r => r.id === rowId);
      const bName = updatesObj.business_name ?? currentRow?.business_name ?? '';
      const lDesc = updatesObj.lead_description ?? currentRow?.lead_description ?? '';
      const namePart = typeof bName === 'string' ? bName.toLowerCase() : '';
      const descPart = typeof lDesc === 'string' ? lDesc.toLowerCase() : '';
      firestoreUpdates.business_name_normalized = `${namePart} ${descPart}`.trim();
      if (updatesObj.lead_description !== undefined) {
        firestoreUpdates.lead_description_normalized = descPart;
      }
    }

    try {
      await updateDoc(rowRef, firestoreUpdates);
    } catch (err) {
      console.error('[BULK-EDIT] Firestore write failed:', err);
    }

    setDbData(prev => prev.map(row => {
      if (row.id !== rowId) return row;
      return { ...row, ...updatesObj, lead_last_edit: firestoreUpdates.lead_last_edit };
    }));
  }, [userId, dbData]);

  const spy = useSpyIntelligence({ userId, handleEdit, handleBulkEdit, displayRows, saveScrollPosition, restoreScrollPosition, fetchData });
  const analyzer = useWebsiteAnalyzer({ userId, handleEdit, displayRows, saveScrollPosition, restoreScrollPosition });
  const potential = usePotentialAnalyzer({ userId, handleEdit, displayRows, saveScrollPosition, restoreScrollPosition });

  // Confirms the Mass Spy review and, for the combo / funnel, chains the DM
  // Email Finder on the freshly-enriched contacts. Shared by the manual
  // "Confirm" button and the funnel's auto-confirm (one-click run).
  const handleMassSpyComboConfirm = useCallback(async () => {
    // Snapshot whether this is an Ultimate Research run before clearing the flag
    // so we can auto-confirm the final DM Email Finder leg too.
    const isFunnelRun = funnelAutoConfirmRef.current === true;
    const funnelStats = funnelStatsRef.current;
    // Clear the funnel auto-confirm flag so a later plain Web Research / combo
    // run is never auto-confirmed by mistake.
    funnelAutoConfirmRef.current = false;
    funnelStatsRef.current = null;
    // Snapshot + clear the combo target up-front so a failing confirm can never
    // leave a stale pending combo behind.
    const comboIds = comboPendingRef.current;
    comboPendingRef.current = null;
    await spy.handleMassSpyReviewConfirm();
    // Combo leg 2: research data is now applied to dbData (optimistically via
    // handleBulkEdit) and a reload is scheduled. Re-open the modal to auto-run
    // the DM Email Finder on the freshly-enriched contacts.
    if (comboIds && comboIds.length > 0) {
      setTimeout(() => {
        const idSet = new Set(comboIds);
        const fresh = dbDataRef.current.filter(c => idSet.has(c.id));
        if (fresh.length > 0) {
          setBulkResearchModal({ isOpen: false, contacts: fresh, autoStartDmFinder: true, autoFinish: isFunnelRun, funnelStats });
        }
      }, 1200);
    }
  }, [spy]);

  // ... (Hier folgen die restlichen Handler-Funktionen, die weitgehend gleich bleiben, aber 'localDbData' durch 'dbData' und 'setLocalDbData' durch 'setDbData' ersetzen)
  // Überarbeitete WhatsApp-Funktion, die direkt einen Link öffnet
  // ── §ACTIONS — Row-level action handlers (email, WhatsApp, spy, similarity) ──

  const handleWhatsAppClick = useCallback((index) => {
    // Speichere die aktuelle Scroll-Position
    saveScrollPosition();
    
    // Prüfe, ob der Index gültig ist
    if (index >= 0 && displayRows && index < displayRows.length) {
      const rowData = displayRows[index];
      
      // Prüfe, ob die Telefonnummer vorhanden ist
      const phoneRaw = rowData.lead_telephone;
      if (!phoneRaw || phoneRaw.trim() === "") {
        alert("Keine Telefonnummer für diesen Kontakt vorhanden.");
        return;
      }
      
      // Extrahiere alle möglichen Telefonnummern
      // Wir verwenden eine Regex, um Telefonnummern zu finden (mit oder ohne Ländervorwahl)
      const phoneMatches = phoneRaw.match(/(?:\+\d{1,4}[\s-]?)?\d+(?:[\s-]?\d+)*/g) || [];
      
      if (phoneMatches.length === 0) {
        alert("Keine gültige Telefonnummer im Format gefunden.");
        return;
      }
      
      // Nehme die erste passende Nummer
      let firstValidPhone = phoneMatches[0];
      
      // Formatiere die Telefonnummer für WhatsApp (entferne alle Nicht-Ziffern außer +)
      const formattedPhone = firstValidPhone.replace(/[^\d+]/g, "");
      
      // WICHTIG: Direktes Update des dbData für sofortige UI-Aktualisierung
      setDbData(prevData => {
        return prevData.map(item => {
          if (item && item.id === rowData.id) {
            return {
              ...item,
              hasWhatsappButtonPressed: true
            };
          }
          return item;
        });
      });
      
      // Zusätzlich auch den editedData aktualisieren
      setEditedData(prevData => ({
        ...prevData,
        [`${rowData.id}-hasWhatsappButtonPressed`]: true
      }));
      
      // Direkte Aktualisierung in der Firebase-Datenbank
      if (userId && rowData.id) {
        const docRef = doc(firestore, `World/${userId}/db/${rowData.id}`);
        updateDoc(docRef, {
          hasWhatsappButtonPressed: true,
          whatsappSentTimestamp: Date.now(),
          whatsappSentBy: userId
        })
        .catch(error => {
          console.error("Fehler beim Aktualisieren des WhatsApp-Status:", error);
        });
      }
      
      const whatsappSettingsRef = ref(database, `/World/${userId}/settings/whatsapp`);
      
      onValue(whatsappSettingsRef, (snapshot) => {
        let whatsappMessage = "";
        let whatsappNumber = formattedPhone;
        
        if (snapshot.exists()) {
          const settings = snapshot.val();
          if (settings.whatsappMessage) {
            whatsappMessage = settings.whatsappMessage.replace(
              /\{\{([a-zA-Z_]+)\}\}/g,
              (match, snippet) => {
                const value = rowData[snippet] || "";
                return value;
              }
            );
          }
          if (settings.whatsappNumber && settings.whatsappNumber.trim() !== "") {
            whatsappNumber = settings.whatsappNumber;
          }
        } else {
          const businessName = rowData.business_name || "";
          if (businessName) {
            whatsappMessage = `Hallo ${businessName}, ich habe Ihre Webseite entdeckt und würde mich gerne über eine mögliche Zusammenarbeit unterhalten.`;
          }
        }
        
        let whatsappUrl = `https://wa.me/${whatsappNumber}`;
        if (whatsappMessage) {
          whatsappUrl += `?text=${encodeURIComponent(whatsappMessage)}`;
        }
        
        window.open(whatsappUrl, "_blank", "noopener,noreferrer");
      }, { onlyOnce: true });
    }
  }, [displayRows, userId, saveScrollPosition]);

  const handleEmailAiClick = useCallback(async (index) => {
    saveScrollPosition();
    // Solange das Popup geöffnet ist, sperren wir die Scroll-Position, um Optimistic Updates
    // davon abzuhalten, den gespeicherten Wert zu überschreiben.
    scrollLockedRef.current = true;
    const rowData = displayRows[index];
    if (!rowData) return;

    const hasReplied = rowData.lead_reply === true;
    let selectedTopic = null;
    let templateOverride = null;

    // Priority 1: an active flow whose current step is a "Send Email" step.
    const flowStep = resolveFlowEmailStep(rowData, warmupFlows);
    if (flowStep?.templateId) {
      const flowTemplate = emailTemplates.find(t => t.id === flowStep.templateId);
      if (flowTemplate) {
        selectedTopic = flowTemplate.topic;
        templateOverride = flowStep.templateId;
      }
    }

    // Priority 2: per-contact template override (lead_templates / lead_template).
    if (!templateOverride) {
      const resolvedTemplateId = resolveTemplateForContact(rowData);
      if (resolvedTemplateId) {
        const overrideTemplate = emailTemplates.find(t => t.id === resolvedTemplateId);
        if (overrideTemplate) {
          selectedTopic = overrideTemplate.topic;
          templateOverride = resolvedTemplateId;
        }
      }
      // Priority 3: AI Tags for replied contacts.
      else if (hasReplied && rowData.tags && typeof rowData.tags === 'object') {
        const tagsArray = Object.values(rowData.tags).sort((a, b) => new Date(b.appliedAt || 0) - new Date(a.appliedAt || 0));
        if (tagsArray.length > 0) {
          const newestTag = tagsArray[0];
          selectedTopic = newestTag.selectedTopic;
        }
      }
    }

    // Pass the flow's sender config so Normal mode can override the sender.
    const flowSenderConfig = (flowStep && flowStep.senderMode && flowStep.senderMode !== 'default')
      ? {
          senderMode: flowStep.senderMode,
          senderId: flowStep.senderId,
          senderPool: flowStep.senderPool,
          flowSenderId: flowStep.flowSenderId,
          flowId: flowStep.flowId,
          flowField: flowStep.flowField,
        }
      : null;

    setEmailPopupData({
      show: true,
      rowData: { ...rowData, selectedTopic, templateOverride, flowSenderConfig },
      index
    });
  }, [displayRows, emailTemplates, warmupFlows, saveScrollPosition]);

  // ── Similarity Engine ──────────────────────────────────────────────
  const handleFindSimilar = useCallback(async (row) => {
    if (!row?.leadSignals) return;
    setSimilarPanel({ show: true, target: row, results: [], loading: true });

    try {
      const candidates = new Map();
      for (const c of dbData) {
        if (c.id !== row.id && c.leadSignals) candidates.set(c.id, c);
      }

      const signals = row.leadSignals;
      const queries = [];
      if (signals.offerType && signals.offerType !== 'unknown') {
        queries.push(
          getDocs(query(
            collection(firestore, `World/${userId}/db`),
            where('leadSignals.offerType', '==', signals.offerType),
            limit(100),
          ))
        );
      }
      if (signals.industry && signals.industry !== 'unknown' && signals.industry !== 'Other') {
        queries.push(
          getDocs(query(
            collection(firestore, `World/${userId}/db`),
            where('leadSignals.industry', '==', signals.industry),
            limit(100),
          ))
        );
      }

      const snapshots = await Promise.all(queries);
      for (const snap of snapshots) {
        snap.forEach(d => {
          if (d.id !== row.id) candidates.set(d.id, { id: d.id, ...d.data() });
        });
      }

      const results = findSimilarContacts(row, Array.from(candidates.values()));
      setSimilarPanel(prev => ({ ...prev, results, loading: false }));
    } catch (err) {
      console.warn('[Similarity] search failed:', err.message);
      const results = findSimilarContacts(row, dbData);
      setSimilarPanel(prev => ({ ...prev, results, loading: false }));
    }
  }, [dbData, userId]);

  const handleSimilarContactClick = useCallback((contact) => {
    setSimilarPanel(p => ({ ...p, show: false }));
    const idx = displayRows.findIndex(r => r.id === contact.id);
    if (idx >= 0) {
      const tableEl = tableRef.current;
      if (tableEl) {
        const rows = tableEl.querySelectorAll('.worldDbTableRow');
        if (rows[idx]) {
          rows[idx].scrollIntoView({ behavior: 'smooth', block: 'center' });
          rows[idx].classList.add('highlight-similar');
          setTimeout(() => rows[idx].classList.remove('highlight-similar'), 1800);
        }
      }
    }
  }, [displayRows]);

  const toggleSelectMode = useCallback(() => {
    setIsSelectMode(prev => {
      if (prev) setSelectedRows({});
      return !prev;
    });
  }, []);

  const toggleRowSelection = useCallback((rowId, isShiftKey = false) => {
    if (isShiftKey && lastSelectedRowId !== null) {
      const lastSelectedIndex = displayRows.findIndex(row => row.id === lastSelectedRowId);
      const currentIndex = displayRows.findIndex(row => row.id === rowId);
      
      if (lastSelectedIndex !== -1 && currentIndex !== -1) {
        const startIdx = Math.min(lastSelectedIndex, currentIndex);
        const endIdx = Math.max(lastSelectedIndex, currentIndex);
        setSelectedRows(prev => {
          const newSelectedRows = { ...prev };
          for (let i = startIdx; i <= endIdx; i++) {
            if (displayRows[i] && displayRows[i].id) {
              newSelectedRows[displayRows[i].id] = true;
            }
          }
          return newSelectedRows;
        });
      }
    } else {
      setSelectedRows(prev => {
        const newValue = !prev[rowId];
        if (newValue) setLastSelectedRowId(rowId);
        return { ...prev, [rowId]: newValue };
      });
    }
  }, [displayRows, lastSelectedRowId]);

  // Firestore allows max 500 writes per atomic batch. We chunk well below
  // that so large bulk deletes (400+ contacts) commit reliably instead of
  // firing hundreds of concurrent deleteDoc requests that can silently fail.
  const FIRESTORE_BATCH_LIMIT = 450;

  // Deletes the given contact ids in atomic chunks and returns the ids that
  // were actually committed. Failed chunks are skipped (and logged) so we
  // never optimistically remove contacts from the UI that still exist on the
  // server — that was the cause of "a few contacts reappear and need a second
  // delete pass".
  const deleteContactsInBatches = async (ids) => {
    const deletedIds = [];
    for (let i = 0; i < ids.length; i += FIRESTORE_BATCH_LIMIT) {
      const chunk = ids.slice(i, i + FIRESTORE_BATCH_LIMIT);
      const batch = writeBatch(firestore);
      chunk.forEach(id => batch.delete(doc(firestore, `World/${userId}/db/${id}`)));
      try {
        await batch.commit();
        deletedIds.push(...chunk);
      } catch (error) {
        console.error(`Error deleting contact batch (${chunk.length} contacts):`, error);
      }
    }
    return deletedIds;
  };

  const executeDeleteSelected = async () => {
    const idsToDelete = Object.keys(selectedRows).filter(id => selectedRows[id]);

    const deletedIds = await deleteContactsInBatches(idsToDelete);
    if (deletedIds.length === 0) return;

    const deletedSet = new Set(deletedIds);
    const newDbData = dbData.filter(row => !deletedSet.has(row.id));
    setDbData(newDbData);
    itemsToLoadRef.current = newDbData.length;

    if (lastDoc && deletedSet.has(lastDoc.id)) {
      if (parentOnReload) parentOnReload();
    } else {
      if (parentOnReload) {
        parentOnReload();
      }
      if (filteredCount !== null) {
        setFilteredCount(prev => prev - deletedIds.length);
      }
    }

    // Keep any contacts that failed to delete selected so the user can retry
    // them directly, instead of clearing the whole selection.
    if (deletedIds.length === idsToDelete.length) {
      setSelectedRows({});
      setIsSelectMode(false);
    } else {
      setSelectedRows(prev => {
        const remaining = {};
        Object.keys(prev).forEach(id => {
          if (prev[id] && !deletedSet.has(id)) remaining[id] = true;
        });
        return remaining;
      });
    }
  };

  const handleDeleteSelected = () => {
    if (selectedCount === 0 || !userId || isDeleting) return;
    const idsToDelete = Object.keys(selectedRows).filter(id => selectedRows[id]);
    const emailsToDelete = idsToDelete
      .map(id => {
        const row = dbData.find(r => r.id === id);
        return row?.lead_email || '';
      })
      .filter(e => e);
    setBlacklistOnDelete(false);
    setDeleteConfirmModal({
      show: true,
      count: selectedCount,
      emails: emailsToDelete,
      contactIds: idsToDelete,
      onConfirm: executeDeleteSelected
    });
  };




  // Handle starting a warmup flow for a contact
  // ── §WARMUP — Flow start/complete/stop/reactivate handlers ──

  const handleStartWarmupFlow = async (contactId, flowId) => {
    const flow = warmupFlows.find(f => f.id === flowId);
    if (!flow) return;

    const contact = dbData.find(c => c.id === contactId);
    const success = await startFlowForContact(userId, contactId, flow, 'warmup');
    if (success) {
      const newWarmupFlow = {
        flowId: flow.id,
        flowName: flow.name,
        startedAt: Date.now(),
        currentStepIndex: 0,
        stepHistory: {},
        nextStepAvailableAt: Date.now() + ((flow.steps[0]?.delayDays || 0) * 24 * 60 * 60 * 1000),
      };

      // Restarting after a completed/stopped run: clear the stale end-state so
      // the contact isn't stuck in "completed", and drop the matching
      // flow_completed_<id> tag for the flow we're restarting.
      const hadEndState = !!(contact?.flowEndStatus || contact?.isDisqualified);
      const completedTagKey = `flow_completed_${flowId}`;
      const hasCompletedTag = !!(contact?.tags && contact.tags[completedTagKey]);
      if (hadEndState || hasCompletedTag) {
        try {
          const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
          await updateDoc(contactRef, {
            flowEndStatus: null,
            flowEndedAt: null,
            isDisqualified: false,
            disqualifiedAt: null,
            disqualifiedReason: null,
            ...(hasCompletedTag ? { [`tags.${completedTagKey}`]: deleteField() } : {}),
            updated_at: Date.now(),
          });
        } catch (_) { /* non-critical: end-state cleanup */ }
      }

      // Update local state — set the new flow and wipe the completed markers so
      // the modal immediately flips to the active-flow view.
      setDbData(prevData => prevData.map(row => {
        if (row.id !== contactId) return row;
        const nextTags = { ...(row.tags || {}) };
        if (hasCompletedTag) delete nextTags[completedTagKey];
        return {
          ...row,
          warmupFlow: newWarmupFlow,
          flow_sort_priority: 2,
          flowEndStatus: null,
          flowEndedAt: null,
          isDisqualified: false,
          disqualifiedAt: null,
          disqualifiedReason: null,
          tags: nextTags,
        };
      }));

      // Keep the modal open so the freshly started flow is shown right away.
      return;
    }
  };

  // Handle completing a warmup flow step.
  // `targetStepIndex` lets the modal target the task it actually renders. When the
  // engine is parked on a ready delay preceding that task, we first resolve the
  // in-between step(s) (no decision) so the decision/action lands on the right node.
  const handleCompleteWarmupStep = async (contactId, decision = null, targetStepIndex = null, interaction = null, options = {}) => {
    const contact = dbData.find(c => c.id === contactId);
    if (!contact?.warmupFlow) return;
    // The picker may report one OR several people contacted at this step.
    const interactions = interaction ? (Array.isArray(interaction) ? interaction : [interaction]) : [];

    const flow = warmupFlows.find(f => f.id === contact.warmupFlow.flowId);
    if (!flow) return;

    setCompletingFlowStep(prev => ({ ...prev, [contactId]: true }));

    // Mark the contact as LinkedIn-inactive BEFORE advancing, so the flow
    // engine's transaction reads the flag and auto-skips any remaining
    // LinkedIn steps in this flow (and future ones).
    if (options.markLinkedInInactive) {
      setDbData(prev => prev.map(r => r.id === contactId ? { ...r, lead_linkedin_inactive: true } : r));
      try {
        const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
        await updateDoc(contactRef, { lead_linkedin_inactive: true });
      } catch (_) { /* non-critical: flag write */ }
    }

    // "Log Notes" step: persist the composed activity note to the contact's
    // lead_notes BEFORE advancing, so it survives regardless of the outcome.
    if (options.logNote && hasNoteContent(options.logNote)) {
      const note = createPrivateNote(options.logNote, {
        source: 'flow',
        flowId: flow.id || contact.warmupFlow?.flowId || null,
        flowName: flow.name || contact.warmupFlow?.flowName || null,
      });
      setDbData(prev => prev.map(r => r.id === contactId
        ? { ...r, lead_notes: [...(Array.isArray(r.lead_notes) ? r.lead_notes : []), note] }
        : r));
      try {
        const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
        await updateDoc(contactRef, { lead_notes: arrayUnion(note), updated_at: Date.now() });
      } catch (_) { /* non-critical: note write */ }
    }

    let flowState = contact.warmupFlow;
    let result = null;
    let guard = 0;

    // Advance until we've completed the intended target step (or the flow ends).
    while (guard++ < 6 && flowState) {
      const idx = flowState.currentStepIndex;
      const isTarget = targetStepIndex == null || idx === targetStepIndex;
      // Only apply the decision on the actual target task; intermediate hops
      // (e.g. resolving a ready delay) advance without a decision.
      // `options.force` bypasses the delay/availability gate — used by the
      // "Force next step (test)" action to skip waits while testing a flow.
      result = await completeFlowStep(userId, contactId, flow, idx, 'warmup', isTarget ? decision : null, { force: !!options.force });
      if (!result.success) break;
      if (result.isComplete || isTarget) break;
      flowState = result.newFlowState;
    }

    if (result && result.success) {
      setDbData(prevData => prevData.map(row => {
        if (row.id !== contactId) return row;
        const updated = { ...row };
        if (result.isComplete) {
          updated.warmupFlow = null;
          updated.flowEndStatus = result.flowEndStatus || 'completed';
          updated.flowEndedAt = Date.now();
          updated.flow_sort_priority = 4;
          if (['disqualified', 'not_interested', 'wrong_contact'].includes(result.flowEndStatus)) {
            updated.isDisqualified = true;
          }
        } else if (result.newFlowState) {
          updated.warmupFlow = result.newFlowState;
          updated.flow_sort_priority = 2;
        }
        if (result.autoAppliedTags) {
          updated.tags = { ...(updated.tags || {}), ...result.autoAppliedTags };
        }
        if (interactions.length) {
          updated.dmInteractions = [...(Array.isArray(row.dmInteractions) ? row.dmInteractions : []), ...interactions];
        }
        return updated;
      }));

      // Persist the decision-maker interaction(s) (who we contacted at this step)
      // so they show up on the next touchpoint. Contact docs are team-writable.
      if (interactions.length) {
        try {
          const existing = Array.isArray(contact.dmInteractions) ? contact.dmInteractions : [];
          const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
          updateDoc(contactRef, { dmInteractions: [...existing, ...interactions] }).catch(() => {});
        } catch (_) { /* non-critical: tracking write */ }
      }

      // Fire-and-forget: log analytics outcomes for decision conditions
      // (kept in sync with LeadSwipe.handleActionAndAdvance).
      // Analytics/audit collections are owner-only by design (GDPR + audit
      // integrity), so team members must not attempt these writes — otherwise
      // Firestore rejects them ("Missing or insufficient permissions").
      if (isOwner && decision) {
        // Swallow rejections: these are async writes, so an unhandled reject
        // would surface as an "Uncaught runtime error" overlay.
        const safe = (p) => { try { p && p.catch && p.catch(() => {}); } catch (_) {} };
        try {
          const decidedIndex = targetStepIndex != null ? targetStepIndex : contact.warmupFlow.currentStepIndex;
          const cond = flow.steps?.[decidedIndex]?.condition;
          if (cond === 'meeting_booked' && decision === 'yes') {
            safe(logOutcomeAnalytics(userId, contactId, { meeting: true }));
          } else if (cond === 'deal_closed' && decision === 'yes') {
            safe(logOutcomeAnalytics(userId, contactId, {
              deal: true,
              dealValue: contact.lead_deal_value ? Number(contact.lead_deal_value) : undefined,
            }));
          } else if (cond === 'responded_positively') {
            safe(logReplyAnalytics(userId, contactId, {
              replyType: decision === 'yes' ? 'positive' : 'objection',
              enthusiasmScore: contact.lead_happyness || null,
              replySubtype: contact.lead_reply_subtype || null,
            }));
          }
        } catch (_) { /* never break flow completion */ }
      }

      if (parentOnReload) parentOnReload();
    }

    setCompletingFlowStep(prev => ({ ...prev, [contactId]: false }));
    if (result && result.isComplete) setFlowSelectModal(null);
  };

  // Handle stopping a warmup flow
  const handleStopWarmupFlow = async (contactId) => {
    const contact = dbData.find(c => c.id === contactId);
    const flow = contact?.warmupFlow ? warmupFlows.find(f => f.id === contact.warmupFlow.flowId) : null;
    
    const success = await stopFlowForContact(userId, contactId, flow, 'warmup');
    if (success) {
      setDbData(prevData => prevData.map(row => {
        if (row.id === contactId) {
          return { ...row, warmupFlow: null, flow_sort_priority: 4 };
        }
        return row;
      }));
    }
    setFlowSelectModal(null);
  };



  const openSocialEditModal = useCallback((rowId, row) => {
    setSocialEditModal({ isOpen: true, rowId, socials: { ...parseSocials(row) } });
  }, []);

  const openNotesModal = useCallback((contact, activity = null) => {
    setNotesModal({ isOpen: true, contact, activity });
  }, []);

  const handleLeadStatusAction = useCallback((actionId) => {
    const contact = dbData.find((row) => row && row.id === leadStatusId);
    if (!contact) return;
    if (actionId === "email" || actionId === "followup") {
      const index = displayRows.findIndex((row) => row.id === contact.id);
      if (index >= 0) handleEmailAiClick(index);
      return;
    }
    if (actionId === "call" || actionId === "meeting") {
      openNotesModal(contact, actionId);
      return;
    }
    if (actionId === "linkedin") {
      window.open(linkedinHref(contact), "_blank", "noopener,noreferrer");
    }
  }, [dbData, leadStatusId, displayRows, handleEmailAiClick, openNotesModal]);

  const openLeadStatus = useCallback((row) => {
    if (!row?.id) return;
    setLeadStatusId((prev) => (prev === row.id ? null : row.id));
  }, []);

  const closeLeadStatus = useCallback(() => setLeadStatusId(null), []);

  const leadStatusContact = useMemo(() => {
    if (!leadStatusId) return null;
    return dbData.find((row) => row && row.id === leadStatusId) || null;
  }, [leadStatusId, dbData]);

  const closeNotesModal = () => {
    setNotesModal({ isOpen: false, contact: null });
  };

  // Jump to a specific contact (e.g. from an Action Center reminder):
  // open its notes/activity, scrolling to its row if it's currently loaded.
  const openContactById = useCallback(async (contactId) => {
    if (!contactId) return;
    let contact = dbData.find(r => r.id === contactId);
    if (!contact && userId) {
      try {
        const snap = await getDoc(doc(firestore, `World/${userId}/db/${contactId}`));
        if (snap.exists()) contact = { id: snap.id, ...snap.data() };
      } catch (err) {
        console.warn('[openContactById] fetch failed:', err.message);
      }
    }
    if (!contact) return;
    setNotesModal({ isOpen: true, contact });

    const idx = displayRows.findIndex(r => r.id === contactId);
    if (idx >= 0 && tableRef.current) {
      const rows = tableRef.current.querySelectorAll('.worldDbTableRow');
      if (rows[idx]) {
        rows[idx].scrollIntoView({ behavior: 'smooth', block: 'center' });
        rows[idx].classList.add('highlight-similar');
        setTimeout(() => rows[idx].classList.remove('highlight-similar'), 1800);
      }
    }
  }, [dbData, displayRows, userId]);

  // Shortcuts fired from the header Tasks modal (see tasks/taskUtils).
  const handleTaskShortcut = useCallback(async (request) => {
    if (!request?.contactId || !userId) return;
    if (request.kind === 'open_notes') {
      openContactById(request.contactId);
      return;
    }
    let contact = dbData.find(r => r.id === request.contactId);
    if (!contact) {
      try {
        const snap = await getDoc(doc(firestore, `World/${userId}/db/${request.contactId}`));
        if (snap.exists()) contact = { id: snap.id, ...snap.data() };
      } catch (err) {
        console.warn('[taskShortcut] contact fetch failed:', err.message);
      }
    }
    if (!contact) return;
    let selectedTopic = null;
    let templateOverride = null;
    if (request.kind === 'template_mail' && request.templateId) {
      const template = emailTemplates.find(t => t.id === request.templateId);
      if (template) {
        selectedTopic = template.topic;
        templateOverride = template.id;
      }
    }
    // An address picked from the task note joins this mail's recipients only;
    // it is not written to the contact.
    let recipientFields = {};
    if (request.recipient) {
      const to = request.recipient.toLowerCase();
      const known = getAllContactEmails(contact).some(e => (e.email || '').toLowerCase() === to);
      recipientFields = {
        taskRecipient: to,
        ...(known ? {} : {
          lead_decision_makers: JSON.stringify([
            ...parseDecisionMakers(contact),
            { name: guessNameFromEmail(to) || null, email: to, role: null, addedFrom: 'task_note' },
          ]),
        }),
      };
    }
    saveScrollPosition();
    scrollLockedRef.current = true;
    setEmailPopupData({
      show: true,
      rowData: {
        ...contact,
        ...recipientFields,
        selectedTopic,
        templateOverride,
        flowSenderConfig: null,
        ...(request.taskId ? { taskId: request.taskId, taskBrief: request.brief || "" } : {}),
      },
      index: -1,
    });
  }, [userId, dbData, emailTemplates, openContactById, saveScrollPosition]);

  useTaskActionListener(TABLE_TASK_ACTIONS, handleTaskShortcut, !!userId);

  useEffect(() => {
    const onPatch = (e) => {
      const { contactId, updates } = e.detail || {};
      if (!contactId || !updates) return;
      setDbData(prev => prev.map(row => (row.id === contactId ? { ...row, ...updates } : row)));
    };
    window.addEventListener(CONTACT_PATCH_EVENT, onPatch);
    return () => window.removeEventListener(CONTACT_PATCH_EVENT, onPatch);
  }, []);

  // Handle reactivating a disqualified contact
  const handleReactivateContact = async (contactId) => {
    try {
      const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
      await updateDoc(contactRef, {
        isDisqualified: false,
        disqualifiedAt: null,
        disqualifiedReason: null,
        flowEndStatus: null,
        flowEndedAt: null,
        flow_sort_priority: 3,
        updated_at: Date.now(),
      });
      
      setDbData(prevData => prevData.map(row => {
        if (row.id === contactId) {
          return { 
            ...row, 
            isDisqualified: false, 
            disqualifiedAt: null,
            disqualifiedReason: null,
            flowEndStatus: null,
            flowEndedAt: null,
            flow_sort_priority: 3,
          };
        }
        return row;
      }));
      setFlowSelectModal(null);
    } catch (error) {
      console.error('Error reactivating contact:', error);
    }
  };

  // Handle opening bulk flow modal
  const handleOpenBulkFlowModal = () => {
    const selectedContactIds = Object.keys(selectedRows).filter(id => selectedRows[id]);
    if (selectedContactIds.length === 0) return;
    setBulkFlowModal(true);
  };

  // Handle starting a flow for multiple contacts
  const handleBulkStartFlow = async (flowId) => {
    const flow = warmupFlows.find(f => f.id === flowId);
    if (!flow) return;

    const selectedContactIds = Object.keys(selectedRows).filter(id => selectedRows[id]);
    if (selectedContactIds.length === 0) return;

    setBulkFlowStarting(true);

    let successCount = 0;
    let skipCount = 0;
    const updatedContacts = [];

    for (const contactId of selectedContactIds) {
      const contact = dbData.find(c => c.id === contactId);
      
      // Skip contacts that already have an active warmup flow
      if (contact?.warmupFlow) {
        skipCount++;
        continue;
      }

      const success = await startFlowForContact(userId, contactId, flow, 'warmup');
      if (success) {
        successCount++;
        updatedContacts.push({
          id: contactId,
          warmupFlow: {
            flowId: flow.id,
            flowName: flow.name,
            startedAt: Date.now(),
            currentStepIndex: 0,
            stepHistory: {},
            nextStepAvailableAt: Date.now() + ((flow.steps[0]?.delayDays || 0) * 24 * 60 * 60 * 1000),
          }
        });
      }
    }

    // Update local state for all successful starts
    if (updatedContacts.length > 0) {
      setDbData(prevData => prevData.map(row => {
        const updated = updatedContacts.find(u => u.id === row.id);
        if (updated) {
          return { ...row, warmupFlow: updated.warmupFlow };
        }
        return row;
      }));
    }

    setBulkFlowStarting(false);
    setBulkFlowModal(false);
    setSelectedRows({});
    setIsSelectMode(false);

    // Show summary
    if (skipCount > 0) {
      alert(`Flow started for ${successCount} contacts. ${skipCount} contacts skipped (already in a flow).`);
    }
  };
  
  const handleOpenBulkTemplateModal = () => {
    const selectedContactIds = Object.keys(selectedRows).filter(id => selectedRows[id]);
    if (selectedContactIds.length === 0) return;
    setTemplateModal({ isOpen: true, contactId: null, isBulk: true, stages: { intro: '', follow_up_1: '', follow_up_2: '', follow_up_3: '' } });
  };

  const handleOpenOutreachJobModal = () => {
    const selectedContactIds = Object.keys(selectedRows).filter(id => selectedRows[id]);
    if (selectedContactIds.length === 0) return;
    setOutreachJobModal({ isOpen: true, assigning: false });
  };

  const encodeRtdbKey = (key) => encodeURIComponent(key).replace(/\./g, '%2E');

  const handleAssignToOutreachJob = async (jobId) => {
    const selectedContactIds = Object.keys(selectedRows).filter(id => selectedRows[id]);
    if (selectedContactIds.length === 0 || !jobId) return;
    setOutreachJobModal(prev => ({ ...prev, assigning: true }));

    try {
      const job = outreachJobs[jobId];
      if (!job) throw new Error("Job not found");
      const pool = job.contactPool || {};

      // Build a lookup map for O(1) access
      const dbMap = new Map(dbData.map(r => [r.id, r]));

      // 1. Enforce job's contactPool filters on each selected contact
      let filterSkipped = 0;
      const afterFilterIds = selectedContactIds.filter(id => {
        const row = dbMap.get(id);
        if (!row) return true; // not in local data — let backend handle it
        const hasAddress = row.has_email || row.lead_email || getAllContactEmails(row).some(e => e.email && !e.emailMissing);
        if (!hasAddress && !jobCanDiscoverEmail(job.steps)) { filterSkipped++; return false; }
        if (pool.statusFilter && (row.lead_contacted || 'Not Contacted') !== pool.statusFilter) { filterSkipped++; return false; }
        if (pool.tagFilter && !(row.tags && row.tags[pool.tagFilter])) { filterSkipped++; return false; }
        if (!contactMatchesPoolLists(row, pool)) { filterSkipped++; return false; }
        return true;
      });

      // 2. Check active queue for contacts already in non-terminal jobs
      const TERMINAL = new Set(["completed", "replied", "failed", "skipped"]);
      const existingQueueSnap = await get(ref(database, `outreachJobQueue/${userId}`));
      const existingQueue = existingQueueSnap.exists() ? existingQueueSnap.val() : {};
      const alreadyInActiveJob = new Set();
      for (const [, entry] of Object.entries(existingQueue)) {
        if (!TERMINAL.has(entry.status)) alreadyInActiveJob.add(entry.contactId || '');
      }

      // 3. Check outreachHistory on each contact (skip if already went through this job)
      let historySkipped = 0;
      const afterQueueFilter = afterFilterIds.filter(id => !alreadyInActiveJob.has(id));
      const historyChecks = afterQueueFilter.map(id => {
        const row = dbMap.get(id);
        if (row?.outreachHistory?.[jobId]) { historySkipped++; return null; }
        return id;
      }).filter(Boolean);

      const eligibleIds = historyChecks;
      const skippedCount = selectedContactIds.length - eligibleIds.length;

      if (eligibleIds.length === 0) {
        const reasons = [];
        if (filterSkipped > 0) reasons.push(`${filterSkipped} don't match job filters${pool.statusFilter ? ` (status: ${pool.statusFilter})` : ''}`);
        if (afterQueueFilter.length < afterFilterIds.length) reasons.push("already in active jobs");
        if (historySkipped > 0) reasons.push("already completed this job");
        alert(`All ${selectedContactIds.length} contacts skipped — ${reasons.join(", ")}.`);
        setOutreachJobModal({ isOpen: false, assigning: false });
        return;
      }

      const now = Date.now();
      const queueUpdates = {};
      for (const id of eligibleIds) {
        queueUpdates[encodeRtdbKey(id)] = {
          contactId: id,
          jobId,
          importName: dbMap.get(id)?.import_name || "",
          statusBeforeJob: dbMap.get(id)?.lead_contacted || "Not Contacted",
          currentStepIndex: 0,
          executeAt: now,
          enteredStepAt: now,
          status: "pending",
          lastStepResult: null,
          stepHistory: {},
          retryCount: 0,
          manualSendDetected: false,
        };
      }
      await update(ref(database, `outreachJobQueue/${userId}`), queueUpdates);

      // Mark contacts as "In Outreach" so they're visible in the table. Prep-only
      // jobs leave the status untouched, same as activation from the job settings.
      if (!job.prepOnly) {
        const BATCH_LIMIT = 499;
        for (let i = 0; i < eligibleIds.length; i += BATCH_LIMIT) {
          const chunk = eligibleIds.slice(i, i + BATCH_LIMIT);
          const b = writeBatch(firestore);
          for (const cid of chunk) {
            b.update(doc(firestore, `World/${userId}/db/${cid}`), { lead_contacted: 'In Outreach' });
          }
          await b.commit();
        }
      }
      await logOutreachEnrollment(firestore, userId, eligibleIds, {
        jobId,
        jobName: job.name,
        detail: 'Added from the contact table',
        at: now,
      });

      // 3. Update job stats + contactIds
      const prevTotal = job.stats?.totalContacts || job.contactIds?.length || 0;
      const prevContactIds = job.contactIds || [];
      await update(ref(database, `World/${userId}/settings/outreachJobs/${jobId}`), {
        "stats/totalContacts": prevTotal + eligibleIds.length,
        contactIds: [...prevContactIds, ...eligibleIds],
        updatedAt: now,
        ...(job.status === "draft" ? { status: "active", startedAt: now } : {}),
      });

      setOutreachJobModal({ isOpen: false, assigning: false });
      setSelectedRows({});
      setIsSelectMode(false);

      const skipReasons = [];
      if (filterSkipped > 0) skipReasons.push(`${filterSkipped} didn't match filters`);
      if (afterQueueFilter.length < afterFilterIds.length) skipReasons.push("some already in active jobs");
      if (historySkipped > 0) skipReasons.push(`${historySkipped} already completed this job`);
      const msg = skippedCount > 0
        ? `${eligibleIds.length} contacts added to "${job.name}". ${skippedCount} skipped (${skipReasons.join(", ")}).`
        : `${eligibleIds.length} contacts added to "${job.name}".`;
      alert(msg);
    } catch (e) {
      console.error("Error assigning to outreach job:", e);
      alert("Error: " + e.message);
      setOutreachJobModal(prev => ({ ...prev, assigning: false }));
    }
  };

  const handleOpenTemplateModal = useCallback((contactId, contactRow) => {
    const stages = {
      intro: contactRow.lead_template || '',
      follow_up_1: contactRow.lead_templates?.follow_up_1 || '',
      follow_up_2: contactRow.lead_templates?.follow_up_2 || '',
      follow_up_3: contactRow.lead_templates?.follow_up_3 || '',
    };
    setTemplateModal({ isOpen: true, contactId, isBulk: false, stages });
  }, []);

  const handleSaveTemplateModal = async () => {
    const { contactId, isBulk, stages } = templateModal;
    setBulkTemplateAssigning(true);

    try {
      const introTemplate = stages.intro || '';
      const stageMap = {};
      Object.entries(stages).forEach(([key, val]) => { if (val) stageMap[key] = val; });
      const hasStageMap = Object.keys(stageMap).length > 0;

      if (isBulk) {
        const selectedContactIds = Object.keys(selectedRows).filter(id => selectedRows[id]);
        if (selectedContactIds.length === 0) return;

        const batch = writeBatch(firestore);
        const updatePayload = {
          lead_template: introTemplate,
          lead_last_edit: { time: new Date().toISOString(), user: userId }
        };
        if (hasStageMap) {
          updatePayload.lead_templates = stageMap;
        } else {
          updatePayload.lead_templates = deleteField();
        }
        selectedContactIds.forEach(cId => {
          batch.update(doc(firestore, `World/${userId}/db/${cId}`), updatePayload);
        });
        await batch.commit();

        setDbData(prevData => prevData.map(row => {
          if (selectedContactIds.includes(row.id)) {
            const updated = { ...row, lead_template: introTemplate };
            if (hasStageMap) { updated.lead_templates = stageMap; }
            else { delete updated.lead_templates; }
            return updated;
          }
          return row;
        }));
        setSelectedRows({});
        setIsSelectMode(false);
      } else {
        const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
        const updatePayload = {
          lead_template: introTemplate,
          lead_last_edit: { time: new Date().toISOString(), user: userId }
        };
        if (hasStageMap) {
          updatePayload.lead_templates = stageMap;
        } else {
          updatePayload.lead_templates = deleteField();
        }
        await updateDoc(contactRef, updatePayload);

        setDbData(prevData => prevData.map(row => {
          if (row.id === contactId) {
            const updated = { ...row, lead_template: introTemplate };
            if (hasStageMap) { updated.lead_templates = stageMap; }
            else { delete updated.lead_templates; }
            return updated;
          }
          return row;
        }));
      }

      setTemplateModal({ isOpen: false, contactId: null, isBulk: false, stages: { intro: '', follow_up_1: '', follow_up_2: '', follow_up_3: '' } });
    } catch (error) {
      console.error('Error saving templates:', error);
      alert('Failed to save templates. Please try again.');
    } finally {
      setBulkTemplateAssigning(false);
    }
  };

  const getSelectedContacts = () => {
    const selectedContactIds = Object.keys(selectedRows).filter(id => selectedRows[id]);
    const selectedContactsComplete = selectedContactIds.map(id => dbData.find(row => row.id === id)).filter(Boolean);
    return selectedContactsComplete.filter(contact => {
        // Consider both the primary lead_email AND any decision-maker emails so
        // that contacts without a lead_email but with a writable decision-maker
        // address are still eligible for Bulk/Auto modes.
        return getAllContactEmails(contact).some(e => e.email && e.email.trim() !== "" && !e.emailMissing);
    });
  };

  // ── §BULK — Bulk operation handlers (AI Manager, AutoSend, Cleaner, Bulk Flow) ──

  const handleOpenAiManager = () => {
    const contacts = getSelectedContacts();
    if (contacts.length === 0) {
        alert("None of the selected contacts have a valid email address.");
        return;
    }
    setBulkSelectedRows(contacts.map(c => c.id));
    setBulkContactsData(contacts);
    setIsAiManagerOpen(true);
  };
  
  const handleOpenAutoSend = () => {
    const contacts = getSelectedContacts();
    if (contacts.length === 0) {
        alert("None of the selected contacts have a valid email address.");
        return;
    }
    setBulkSelectedRows(contacts.map(c => c.id));
    setBulkContactsData(contacts);
    setIsAutoSendOpen(true);
  };

  // Open the Bulk sender pre-loaded with backend-prepared emails (written by a
  // flow's prepare step or an outreach job in prepare mode). Bulk mode then seeds
  // each contact instantly from its preparedEmails doc — no live AI generation,
  // no waiting.
  //
  // Without arguments it loads every prepared contact, fetched fresh rather than
  // from the current page so nothing is missed. Pass contact IDs to open just
  // those — that's how a single card in the Prepared Mails strip opens.
  //
  // Which contacts to open comes from the preparedEmails collection, i.e. exactly
  // what the strip renders. Resolving them through the contacts'
  // `has_prepared_email` flag instead made a visible card openable only as long
  // as that flag agreed, so a drifted flag produced "nothing is waiting" while
  // the card sat right there.
  const handleOpenPreparedBulk = async (contactIds = null) => {
    if (!userId || isLoadingPrepared) return;
    setIsLoadingPrepared(true);
    try {
      const wanted = Array.isArray(contactIds) ? contactIds.filter(Boolean) : null;
      const preparedIds = wanted && wanted.length > 0
        ? wanted
        // Oldest first, so stepping through drains the backlog. The strip shows
        // newest first (fresh arrivals belong on the left), and reviewing in that
        // same order let the oldest drafts starve: new mails kept landing ahead of
        // them, so the ones waiting longest were never reached.
        // Cap the batch so a huge backlog can't open an unmanageable session.
        : (await getDocs(query(
          collection(firestore, `World/${userId}/preparedEmails`),
          orderBy('generatedAt', 'asc'),
          limit(200),
        ))).docs.map(d => d.id);

      const contactSnaps = await Promise.all(
        preparedIds.map(id => getDoc(doc(firestore, `World/${userId}/db/${id}`)))
      );

      // The contact can vanish from under a waiting draft (blacklist removal
      // after research, AI Cleaner, duplicate merge). That draft can never be
      // sent, so throw it away instead of leaving a card that opens nothing.
      // Deleting it also releases the outreach job's parked queue slot.
      const orphanIds = preparedIds.filter((_, i) => !contactSnaps[i].exists());
      if (orphanIds.length > 0) {
        await Promise.all(orphanIds.map(id => (
          deleteDoc(doc(firestore, `World/${userId}/preparedEmails/${id}`)).catch(() => {})
        )));
      }

      const existing = contactSnaps.filter(s => s.exists());
      const contacts = existing
        // The prepared doc exists, so the flag is true whatever the contact says.
        // Bulk mode keys its "seed instead of generate" decision off this field —
        // without it a drifted flag would silently re-generate a paid draft.
        .map(s => ({ id: s.id, ...s.data(), has_prepared_email: true }))
        .filter(c => getAllContactEmails(c).some(e => e.email && e.email.trim() !== "" && !e.emailMissing));

      if (contacts.length === 0) {
        setPreparedCountTrigger(t => t + 1);
        if (orphanIds.length > 0) {
          alert(orphanIds.length === 1
            ? "That contact was deleted in the meantime — the prepared email has been discarded."
            : `${orphanIds.length} contacts were deleted in the meantime — their prepared emails have been discarded.`);
        } else if (existing.length > 0) {
          alert("These contacts no longer have a usable email address.");
        } else {
          alert("No prepared emails are waiting anymore.");
        }
        return;
      }

      setBulkSelectedRows(contacts.map(c => c.id));
      setBulkContactsData(contacts);
      setIsAiManagerOpen(true);
    } catch (err) {
      console.error("Error opening prepared emails:", err);
      alert("Could not load the prepared emails. Please try again.");
    } finally {
      setIsLoadingPrepared(false);
    }
  };

  const handleOpenAiCleaner = () => {
    const selectedContactIds = Object.keys(selectedRows).filter(id => selectedRows[id]);
    const contacts = selectedContactIds.map(id => dbData.find(row => row.id === id)).filter(Boolean);
    if(contacts.length === 0) return;
    setBulkSelectedRows(selectedContactIds);
    setBulkContactsData(contacts);
    document.body.classList.add('ai-cleaner-open');
    setIsAiCleanerOpen(true);
  };

  const closeBulkPopups = () => {
    setIsAiManagerOpen(false);
    setIsAutoSendOpen(false);
    setBulkSelectedRows([]);
    setBulkContactsData([]);
    setSelectedRows({});
    setIsSelectMode(false);
    // Prepared emails may have been sent in this session — re-check the count so
    // the nav button updates (and hides once the backlog is cleared).
    setPreparedCountTrigger(t => t + 1);
  };

  const closeAiCleaner = (results = null) => {
    setIsAiCleanerOpen(false);
    document.body.classList.remove('ai-cleaner-open');
    if (results) {
      if (results.removed > 0) {
        onReload();
      } else if (results.renamed && results.renamed.length > 0) {
        setDbData(prevData => {
          const newData = [...prevData];
          results.renamed.forEach(renamedContact => {
            const index = newData.findIndex(item => item.id === renamedContact.id);
            if (index !== -1) {
              newData[index] = {
                ...newData[index],
                business_name: renamedContact.cleaned,
                lead_description: renamedContact.lead_description,
              };
            }
          });
          return newData;
        });
      }
    }
    setCleanerResults(null);
    setBulkSelectedRows([]);
    setBulkContactsData([]);
    setSelectedRows({});
    setIsSelectMode(false);
  };

  const handleSelectFirst = (count) => {
    if (!displayRows || displayRows.length === 0) return;
    
    // Nimm die ersten 'count' Einträge aus den aktuell gefilterten und sortierten Ergebnissen
    const toSelect = displayRows.slice(0, count);
    const newSelectedRows = {};
    
    toSelect.forEach(row => {
      if (row && row.id) {
        newSelectedRows[row.id] = true;
      }
    });
    
    setSelectedRows(newSelectedRows);
    setIsSelectMode(true);
    
    // Setze den letzten ausgewählten für Shift-Click Funktionalität
    if (toSelect.length > 0 && toSelect[toSelect.length - 1].id) {
      setLastSelectedRowId(toSelect[toSelect.length - 1].id);
    }
  };


  const executeDeleteNoEmailEntries = async () => {
    const contactsToDelete = spy.massSpyContacts.filter(c => !c._emailFound).map(c => c.id);

    const deletedIds = await deleteContactsInBatches(contactsToDelete);
    if (deletedIds.length === 0) return;

    const deletedSet = new Set(deletedIds);
    const newDbData = dbData.filter(row => !deletedSet.has(row.id));
    setDbData(newDbData);
    itemsToLoadRef.current = newDbData.length;

    if (lastDoc && deletedSet.has(lastDoc.id)) {
        if (parentOnReload) parentOnReload();
    } else {
        if (parentOnReload) {
            parentOnReload();
        }
        if (filteredCount !== null) {
            setFilteredCount(prev => prev - deletedIds.length);
        }
    }
  };

  const handleDeleteNoEmailEntries = () => {
    if (!userId || isDeleting) return;
    const contactsToDelete = spy.massSpyContacts.filter(c => !c._emailFound).map(c => c.id);
    if (contactsToDelete.length === 0) return;
    setDeleteConfirmModal({
      show: true,
      count: contactsToDelete.length,
      onConfirm: executeDeleteNoEmailEntries
    });
  };

  const addEmailsToBlacklist = async (emails) => {
    if (!userId || emails.length === 0) return;

    // De-dupe so the same email never produces two writes in one batch
    // (Firestore rejects batches that touch the same doc twice).
    const uniqueEmails = [...new Set(
      emails.map(email => email.toLowerCase().trim()).filter(Boolean)
    )];

    // The contacts are deleted right after this runs, so we only need to
    // persist the blacklist entries themselves — updating globalOptOut on the
    // soon-to-be-deleted contact docs would be wasted work. Batching keeps
    // large blacklist-on-delete operations fast and reliable.
    for (let i = 0; i < uniqueEmails.length; i += FIRESTORE_BATCH_LIMIT) {
      const chunk = uniqueEmails.slice(i, i + FIRESTORE_BATCH_LIMIT);
      const batch = writeBatch(firestore);
      chunk.forEach(emailLower => {
        const blacklistRef = doc(firestore, `World/${userId}/blacklist/${emailLower}`);
        batch.set(blacklistRef, {
          email: emailLower,
          addedAt: Date.now(),
          source: 'bulk_delete',
          reason: 'Blacklisted during bulk delete'
        });
      });
      try {
        await batch.commit();
      } catch (error) {
        console.error(`Error adding blacklist batch (${chunk.length} emails):`, error);
      }
    }
  };

  const confirmDelete = async (withBlacklist = false) => {
    if (!deleteConfirmModal.onConfirm) return;
    setIsDeleting(true);
    try {
      if (withBlacklist && deleteConfirmModal.emails?.length > 0) {
        await addEmailsToBlacklist(deleteConfirmModal.emails);
      }
      await deleteConfirmModal.onConfirm();
    } finally {
      setIsDeleting(false);
      setBlacklistOnDelete(false);
      setDeleteConfirmModal({ show: false, count: 0, onConfirm: null, emails: [], contactIds: [] });
    }
  };

  const cancelDelete = () => {
    if (isDeleting) return;
    setBlacklistOnDelete(false);
    setDeleteConfirmModal({ show: false, count: 0, onConfirm: null, emails: [], contactIds: [] });
  };

  const openAiManagerWithCleanedContacts = (contactIds, contactsData) => {
    setBulkSelectedRows(contactIds);
    setBulkContactsData(contactsData);
    setIsAiManagerOpen(true);
    setCleanerResults(null);
  };

  const openAutoSendWithCleanedContacts = (contactIds, contactsData) => {
    setBulkSelectedRows(contactIds);
    setBulkContactsData(contactsData);
    setIsAutoSendOpen(true);
    setCleanerResults(null);
  };

  const handleCleanerFlowAssigned = (contactIds, flow, successCount, skipCount) => {
    setDbData(prevData => prevData.map(row => {
      if (contactIds.includes(row.id) && !row.warmupFlow) {
        return {
          ...row,
          warmupFlow: {
            flowId: flow.id,
            flowName: flow.name,
            startedAt: Date.now(),
            currentStepIndex: 0,
            stepHistory: {},
            nextStepAvailableAt: Date.now() + ((flow.steps[0]?.delayDays || 0) * 24 * 60 * 60 * 1000),
          }
        };
      }
      return row;
    }));
    setCleanerResults(null);
  };

  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
  };

  const handleImportFilterChange = (newImport) => {
    setImportFilter(newImport);
  };

  const handleFlowFilterChange = (newFlow) => {
    setFlowFilter(newFlow);
    // Reset step filter when flow changes
    setFlowStepFilter('all');
  };

  const handleFlowStepFilterChange = (newStep) => {
    setFlowStepFilter(newStep);
  };

  const handleSearch = useCallback((newSearchTerm) => {
    setSearchTerm(newSearchTerm);
  }, []);

  const handleAdvancedFilterChange = (e) => {
    const { name, value } = e.target;
    setAdvancedFilters(prev => ({ ...prev, [name]: value }));
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters({ ...DEFAULT_ADVANCED_FILTERS });
  };

  const sortableColumns = useMemo(() => [
    "lead_rating",
    "lead_reviews",
    "lead_instagram_followers",
    "lead_stars",
    "lead_score",
    "business_name",
    "lead_email",
    "lead_website",
    "lead_telephone",
    "lead_description",
    "lead_socials",
    "lead_decision_makers",
    "lead_template",
    "lead_reply",
    "lead_instagram",
    "lead_city",
    "timestamp",
    "import_name",
    "contacted",
    ...customFields.map((cf) => cf.key),
  ], [customFields]);

  // A single skeleton placeholder row. Shared between the loading state and the
  // not-yet-revealed slots so the keys stay stable and React reuses the DOM
  // (no blank flash when real rows take over).
  const renderSkeletonRow = (i) => (
    <tr key={`skel-${i}`} className="skeleton-row">
      {orderedColumns.map((colKey) => {
        if (colKey === "selectCheckbox") return isSelectMode ? <td key={colKey} className="worldDbTableCell selectCheckbox"><div className="skel-box skel-check" /></td> : null;
        if (colKey === "rowNumber") return <td key={colKey} className="worldDbTableCell"><div className="skel-box skel-nr" /></td>;
        if (colKey === "warmupFlow") return <td key={colKey} className="worldDbTableCell"><div className="skel-box skel-circle skel-flow" /></td>;
        if (colKey === "contacted") return <td key={colKey} className="worldDbTableCell"><div className="skel-box skel-badge" /></td>;
        if (colKey === "aiMail") return <td key={colKey} className="worldDbTableCell"><div className="skel-box skel-badge" /></td>;
        if (colKey === "spy") return <td key={colKey} className="worldDbTableCell"><div className="skel-box skel-circle" /></td>;
        if (colKey === "intelligence") return <td key={colKey} className="worldDbTableCell"><div className="skel-box skel-circle" /></td>;
        if (colKey === "business_name") return <td key={colKey} className="worldDbTableCell"><div className="skel-box skel-name" /></td>;
        if (colKey === "lead_description") return <td key={colKey} className="worldDbTableCell"><div className="skel-box skel-desc" /></td>;
        if (colKey === "lead_email") return <td key={colKey} className="worldDbTableCell"><div className="skel-box skel-email" /></td>;
        return <td key={colKey} className="worldDbTableCell"><div className="skel-box skel-default" /></td>;
      })}
    </tr>
  );

  // Vollbild-Loader nur, solange die Spaltenstruktur (Custom Fields /
  // Spaltenreihenfolge) noch unbekannt ist — d.h. praktisch nur beim allerersten
  // Kaltstart ohne Cache. Sobald die Spalten feststehen, rendern wir direkt die
  // Tabelle: während die Daten laden zeigt sie das Skeleton, das danach in
  // Echtzeit von oben nach unten durch die echten Zeilen ersetzt wird.
  if (!initialReady && customFieldsLoading) {
    return (
      <div className="worldDbTableContainer initial-loading" ref={tableContainerRef}>
        <div className="table-loading-bar-container">
          <div className="table-loading-logo-spin">
            <img src="/img/salesgod_dark.webp" alt="SalesGod" />
          </div>
          <div className="table-loading-bar">
            <div className="table-loading-bar-fill"></div>
          </div>
        </div>
      </div>
    );
  }

  // ── §RENDER — Component JSX ──
  // Layout: ActionCenter → Nav → AdvancedFilters → Table → Modals/Popups
  return (
    <div className="worldDbTableContainer" ref={tableContainerRef}>
      <IntelligenceErrorBoundary label="ActionCenter" silent>
        <ActionCenter userId={userId} onOpenContact={openContactById} onFilterSegment={(payload) => {
          setStatusFilter('all');
          setSegmentFilter(payload?.segment || null);
          if (tableContainerRef.current) tableContainerRef.current.scrollTop = 0;
        }} />
      </IntelligenceErrorBoundary>
      {segmentFilter && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', background: '#1a1a2e', borderRadius: 8, margin: '4px 10px', fontSize: 12, color: '#7c9aed' }}>
          <span style={{ fontWeight: 600 }}>Segment-Fokus:</span>
          {segmentFilter.offerType && segmentFilter.offerType !== 'unknown' && <span>{segmentFilter.offerType}</span>}
          {segmentFilter.b2b_b2c && segmentFilter.b2b_b2c !== 'unknown' && <span>· {segmentFilter.b2b_b2c.toUpperCase()}</span>}
          {segmentFilter.industry && segmentFilter.industry !== 'unknown' && <span>· {segmentFilter.industry}</span>}
          {segmentFilter.sizeProxy && segmentFilter.sizeProxy !== 'unknown' && <span>· {segmentFilter.sizeProxy}</span>}
          <button onClick={() => setSegmentFilter(null)} style={{ background: 'none', border: '1px solid #444', borderRadius: 6, color: '#aaa', cursor: 'pointer', padding: '2px 8px', fontSize: 11, marginLeft: 'auto' }}>✕ Aufheben</button>
        </div>
      )}
      {/* Phase E (2026-08): Dedup-Alerts-Badge wird inline in der Nav neben
          dem Kontakte-Zähler gerendert (siehe WorldDbTableNav). Rendert
          nichts, wenn keine offenen Alerts existieren — hält das UI ruhig,
          wird erst laut wenn das System tatsächlich einen Fall zum Reviewen
          aufwirft. */}
      <WorldDbTableNav
        userId={userId}
        onOpenDuplicateAlert={handleOpenDuplicateAlertFromPanel}
        setShowUploader={setShowUploader}
        setShowCreator={setShowCreator}
        displayedCount={displayedCount}
        filteredCount={filteredCount}
        contactedToday={contactedToday}
        isSelectMode={isSelectMode}
        toggleSelectMode={toggleSelectMode}
        selectedCount={selectedCount}
        handleDeleteSelected={handleDeleteSelected}
        isDeleting={isDeleting}
        totalDatabaseCount={totalDatabaseCount}
        handleOpenAiManager={handleOpenAiManager}
        handleOpenAutoSend={handleOpenAutoSend}
        handleStartMassSpy={() => {
          const ids = Object.keys(selectedRows).filter(id => selectedRows[id]);
          const contacts = displayRows.filter(r => r?.id && ids.includes(r.id));
          setBulkResearchModal({ isOpen: true, contacts });
        }}
        handleOpenAiCleaner={handleOpenAiCleaner}
        handleBulkStartFlow={handleOpenBulkFlowModal}
        handleBulkAssignTemplate={handleOpenBulkTemplateModal}
        hasEmailTemplates={emailTemplates.length > 0}
        hasWarmupFlows={warmupFlows.length > 0}
        hasOutreachJobs={hasOutreachJobs && Object.keys(outreachJobs).length > 0}
        handleOpenOutreachJobModal={handleOpenOutreachJobModal}
        statusFilterMode={statusFilter}
        onStatusFilterChange={handleStatusFilterChange}
        importFilterMode={importFilter}
        onImportFilterChange={handleImportFilterChange}
        importOptions={importOptions}
        flowFilterMode={flowFilter}
        onFlowFilterChange={handleFlowFilterChange}
        flowStepFilterMode={flowStepFilter}
        onFlowStepFilterChange={handleFlowStepFilterChange}
        warmupFlows={warmupFlows}
        onSearch={handleSearch}
        searchTerm={searchTerm}
        isSearching={isSearching}
        needsMigration={needsMigration}
        onMigrate={handleMigration}
        isMigrating={isMigrating}
        migrationProgress={migrationProgress}
        onSelectFirst={handleSelectFirst}
        onToggleAdvancedFilters={() => setShowAdvancedFilters(!showAdvancedFilters)}
        isAdvancedFilterVisible={showAdvancedFilters}
        isDataLoaded={!loading || dbData.length > 0}
        showAutoSendButton={
          // Zeige den Button wenn:
          // 1. Owner UND hasAutoSendButton aktiv ODER
          // 2. Team-Mitglied mit autoSendButton Feature (unabhängig vom Owner-Flag)
          isOwner 
            ? ownerHasAutoSendButton 
            : (teamFeatures?.includes('autoSendButton') || ownerHasAutoSendButton)
        }
      />
      <AdvancedFiltersPanel isOpen={showAdvancedFilters} filters={advancedFilters} onChange={handleAdvancedFilterChange} onReset={resetAdvancedFilters} />
      {/* Emails written in the background overnight, waiting to be sent. Renders
          nothing when none are waiting. */}
      <PreparedMailsStrip
        userId={userId}
        onOpenPrepared={handleOpenPreparedBulk}
        isLoadingPrepared={isLoadingPrepared}
      />
      <div className="table-content-wrapper">
        <table
          className="worldDbTable"
          onWheel={handleWheel}
          ref={tableRef}
          style={{
            ...(frozenLayout ? { tableLayout: "fixed", minWidth: frozenLayout.totalWidth } : {}),
            opacity: isFilterPending ? 0.55 : 1,
            transition: "opacity 0.15s ease",
          }}
        >
          {frozenLayout && (
            <colgroup>
              {frozenLayout.cols.map(({ colKey, width }) => (
                <col key={colKey} style={{ width }} />
              ))}
            </colgroup>
          )}
          <thead>
            <tr>
              {orderedColumns.map((colKey) => {
                if (colKey === "selectCheckbox") {
                  return isSelectMode ? <th key={colKey} className="worldDbTableHeader selectCheckbox" data-column={colKey}><div></div></th> : null;
                }
                const headerLabel = availableFields[colKey] || {
                    rowNumber: "Nr.", warmupFlow: "Flow", status: "Status", lead_description: "Description",
                    timestamp: "Days", lastEdited: "Last Edited", contacted: "Lead Status",
                    aiMail: "AI Mail", spy: "Research", whatsapp: "WhatsApp", websiteAnalysis: "Website Analysis", intelligence: "BonusClub Intelligence"
                }[colKey] || colKey;
                const isSortable = sortableColumns.includes(colKey);
                return (
                  <th key={colKey} className={`worldDbTableHeader ${colKey} ${isSortable ? 'sortable' : ''}`} data-column={colKey}>
                    <div className="column-header-content">
                      {headerLabel}
                      {renderSortButtons(colKey)}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {(loading && !loadingMore) || (!loadingMore && !noFilterResults && displayRows.length > 0 && revealCount === 0) ? (
              Array.from({ length: SKELETON_COUNT }).map((_, i) => renderSkeletonRow(i))
            ) : noFilterResults ? (
              <tr className="no-results-row">
                <td colSpan={orderedColumns.length + (isSelectMode ? 1 : 0)} className="no-results-cell">
                  <div className="no-results-content">
                    <div className="no-results-icon">
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 18a8 8 0 1 1 8-8 8.009 8.009 0 0 1-8 8zm0-14a6 6 0 1 0 0 12 6 6 0 0 0 0-12z" fill="currentColor" />
                        <path d="M21.707 20.293l-4-4a1 1 0 0 0-1.414 1.414l4 4a1 1 0 0 0 1.414-1.414z" fill="currentColor" />
                        <path d="M14 9H6a1 1 0 0 0 0 2h8a1 1 0 0 0 0-2z" fill="currentColor" />
                      </svg>
                    </div>
                    <h3 className="no-results-title">No matching contacts found</h3>
                    <p className="no-results-description">
                      No contacts were found that match the current filter criteria.
                    </p>
                    <button 
                      className="no-results-reset-btn"
                      onClick={() => {
                        handleSearch('');
                        handleStatusFilterChange('all');
                        handleImportFilterChange('all');
                        handleFlowFilterChange('all');
                        handleFlowStepFilterChange('all');
                        handleSortChange('timestamp', 'desc');
                      }}
                    >
                       <svg width="14" height="14" viewBox="0 0 24 24" fill="none" >
                         <path d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" fill="currentColor" />
                       </svg>
                      Reset Filters
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              displayRows.map((row, index) => (
                index < SKELETON_COUNT && index >= revealCount ? (
                  renderSkeletonRow(index)
                ) : (
                <WorldDbTableRow
                  key={row.id ? `${row.id}_${index}` : index}
                  row={row}
                  rowId={row.id}
                  index={index}
                  orderedColumns={orderedColumns}
                  isSelectMode={isSelectMode}
                  selectedRows={selectedRows}
                  editedData={editedData}
                  warmupFlows={warmupFlows}
                  completingFlowStep={completingFlowStep}
                  emailTemplates={emailTemplates}
                  userId={userId}
                  customFieldTypes={customFieldTypes}
                  spyLoadingRows={spy.spyLoadingRows}
                  analyzerLoadingRows={analyzer.analyzerLoadingRows}
                  potentialLoadingRows={potential.potentialLoadingRows}
                  onToggleRowSelection={toggleRowSelection}
                  onEdit={handleUserEdit}
                  onEmailAiClick={handleEmailAiClick}
                  onSpyClick={setSpySelectorRow}
                  onWhatsAppClick={handleWhatsAppClick}
                  onOpenFlowSelect={setFlowSelectModal}
                  onOpenDecisionMakers={setDecisionMakersModal}
                  onOpenSocialEdit={openSocialEditModal}
                  onOpenNotes={openNotesModal}
                  onOpenLeadStatus={openLeadStatus}
                  statusPanelOpen={leadStatusId === row.id}
                  onOpenIntel={setIntelModal}
                  onOpenIgResearch={setIgResearchContact}
                  onOpenSeoResearch={setSeoResearchContact}
                  onFindSimilar={handleFindSimilar}
                  onOpenTemplateModal={handleOpenTemplateModal}
                  onToggleFavorite={handleToggleFavorite}
                  duplicateAlert={duplicateAlertMap.get(row.id) || null}
                  onOpenDuplicateAction={handleOpenDuplicateAction}
                />
                )
              ))
            )}
            {hasMoreData && !noFilterResults && !(loading && !loadingMore) && (
              <tr className="load-more-row">
                <td colSpan={orderedColumns.length + (isSelectMode ? 1 : 0)} className="load-more-cell">
                  <div className="table-load-more-container">
                    <button className="table-load-more-button" onClick={loadMoreData} disabled={loadingMore}>
                      {loadingMore ? 'Loading...' : 'Load More Contacts'}
                    </button>
                    <span className="table-load-more-info">
                      {displayedCount} of {filteredCount ?? totalDatabaseCount} contacts displayed
                    </span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      <EmailPopup 
        isPopupOpen={emailPopupData.show}
        displayRows={displayRows}
        selectedRowIndex={emailPopupData.index}
        userId={userId}
        onEmailResult={handleEmailResult}
        closePopup={() => setEmailPopupData({ show: false, rowData: null, index: null })}
        rowData={emailPopupData.rowData}
      />

      <BulkEmailPopup 
        isAiManagerOpen={isAiManagerOpen}
        isAutoSendOpen={isAutoSendOpen}
        bulkSelectedRows={bulkSelectedRows}
        bulkContactsData={bulkContactsData}
        localDbData={dbData}
        userId={userId}
        onEmailResult={handleEmailResult}
        closeAiManager={closeBulkPopups}
        closeAutoSend={closeBulkPopups}
      />

      <MassSpyPopup
        isMassSpyActive={spy.isMassSpyActive}
        massSpyProgress={spy.massSpyProgress}
        massSpyTotal={spy.massSpyTotal}
        massSpyQueue={spy.massSpyQueue}
        massSpyContacts={spy.massSpyContacts}
        massSpyResults={spy.massSpyResults}
        massSpyProcessedData={spy.massSpyProcessedData}
        showMassSpyReview={spy.showMassSpyReview}
        activeModules={spy.spySettings?.activeModules}
        autoConfirmReview={funnelAutoConfirmRef.current}
        closeMassSpy={() => { comboPendingRef.current = null; funnelAutoConfirmRef.current = false; funnelStatsRef.current = null; spy.closeMassSpy(); }}
        onDeleteNoEmailEntries={handleDeleteNoEmailEntries}
        onReviewConfirm={handleMassSpyComboConfirm}
        onReviewCancel={() => { comboPendingRef.current = null; funnelAutoConfirmRef.current = false; funnelStatsRef.current = null; spy.handleMassSpyReviewCancel(); }}
      />

      {analyzer.bulkProgress && (
        <div className="wa-bulk-toast">
          <i className={`fa ${analyzer.bulkProgress.finished ? 'fa-check-circle' : 'fa-spinner fa-spin'}`} />
          <span>
            Website Analyzer: {analyzer.bulkProgress.done}/{analyzer.bulkProgress.total} scanned
            {analyzer.bulkProgress.failed > 0 && ` (${analyzer.bulkProgress.failed} failed)`}
            {analyzer.bulkProgress.finished && ' — Done!'}
          </span>
        </div>
      )}

      {potential.bulkProgress && (
        <div className="wa-bulk-toast">
          <i className={`fa ${potential.bulkProgress.finished ? 'fa-check-circle' : 'fa-spinner fa-spin'}`} />
          <span>
            Potential Analyser: {potential.bulkProgress.done}/{potential.bulkProgress.total} bewertet
            {potential.bulkProgress.failed > 0 && ` (${potential.bulkProgress.failed} fehlgeschlagen)`}
            {potential.bulkProgress.finished && ' — Fertig!'}
          </span>
        </div>
      )}
      
      {isAiCleanerOpen && (
        <AiCleanerPopup 
          isOpen={isAiCleanerOpen}
          bulkSelectedRows={bulkSelectedRows}
          bulkContactsData={bulkContactsData}
          userId={userId}
          closeAiCleaner={closeAiCleaner}
          onOpenAiManager={openAiManagerWithCleanedContacts}
          onOpenAutoSend={openAutoSendWithCleanedContacts}
          showAutoSendButton={
            isOwner 
              ? ownerHasAutoSendButton 
              : (teamFeatures?.includes('autoSendButton') || ownerHasAutoSendButton)
          }
          warmupFlows={warmupFlows}
          onFlowAssigned={handleCleanerFlowAssigned}
        />
      )}

      <SpySelectorPopup
        isOpen={spySelectorRow !== null}
        onClose={() => setSpySelectorRow(null)}
        onSelectSpy={(spyId) => {
          const idx = spySelectorRow;
          setSpySelectorRow(null);
          if (spyId === 'web_intelligence') {
            spy.handleSpyClick(idx);
          } else if (spyId === 'website_analyzer') {
            analyzer.handleAnalyzeClick(idx);
          } else if (spyId === 'instagram_research') {
            setIgResearchContact(displayRows[idx] || null);
          } else if (spyId === 'seo_analyzer') {
            setSeoResearchContact(displayRows[idx] || null);
          } else if (spyId === 'potential_analyzer') {
            potential.handleAnalyzePotentialClick(idx);
          }
        }}
        contactData={spySelectorRow !== null ? displayRows[spySelectorRow] : null}
        isLoading={spySelectorRow !== null && displayRows[spySelectorRow]
          ? (spy.spyLoadingRows[displayRows[spySelectorRow].id] === true || analyzer.analyzerLoadingRows[displayRows[spySelectorRow].id] === true || potential.potentialLoadingRows[displayRows[spySelectorRow].id] === true)
          : false}
        research={research}
        researchLoading={researchLoading}
        researchContext={researchContext}
        seoKeyReady={seoKeyReady}
        potentialEnabled={potential.paSettings?.enabled === true}
        analyzerCreditCost={analyzer.waSettings?.activeModules ? calculateWebsiteAnalyzerCost(analyzer.waSettings.activeModules) : null}
      />

      <SpyDataPreview
        isOpen={spy.spyDataPreview.isOpen}
        onClose={spy.handleSpyPreviewClose}
        onApply={spy.handleSpyPreviewApply}
        onSelectiveApply={spy.handleSpyPreviewSelectiveApply}
        intelligenceData={spy.spyDataPreview.intelligenceData}
        contactData={spy.spyDataPreview.contactData}
        blacklistedEmails={spy.spyDataPreview.blacklistedEmails}
      />

      {/* Blacklist removal confirmation — appears after Web Research /
          Mass Spy when newly-discovered emails matched the blacklist.
          One-click removal of the affected contacts. */}
      {spy.blacklistRemovalQueue.length > 0 && (
        <div className="bl-removal-overlay" onClick={spy.removingBlacklistContacts ? undefined : spy.dismissBlacklistRemoval}>
          <div className="bl-removal-modal" onClick={e => e.stopPropagation()}>
            <div className="bl-removal-header">
              <div className="bl-removal-icon"><i className="fa fa-ban" /></div>
              <div className="bl-removal-title">
                <h3>{spy.blacklistRemovalQueue.length} Contact{spy.blacklistRemovalQueue.length === 1 ? '' : 's'} → Blacklist</h3>
                <p>
                  Web Research discovered emails on your blacklist for the contact{spy.blacklistRemovalQueue.length === 1 ? '' : 's'} below.
                  Remove {spy.blacklistRemovalQueue.length === 1 ? 'it' : 'them'} from your list?
                </p>
              </div>
            </div>
            <div className="bl-removal-list">
              {spy.blacklistRemovalQueue.slice(0, 10).map(entry => (
                <div key={entry.id} className="bl-removal-row">
                  <span className="bl-removal-row-name">{entry.name}</span>
                  <span className="bl-removal-row-email">
                    {entry.blockedEmails[0]}{entry.blockedEmails.length > 1 ? ` (+${entry.blockedEmails.length - 1})` : ''}
                  </span>
                </div>
              ))}
              {spy.blacklistRemovalQueue.length > 10 && (
                <div className="bl-removal-more">+{spy.blacklistRemovalQueue.length - 10} more</div>
              )}
            </div>
            <div className="bl-removal-footer">
              <button
                className="bl-removal-cancel"
                onClick={spy.dismissBlacklistRemoval}
                disabled={spy.removingBlacklistContacts}
              >
                Keep Contacts
              </button>
              <button
                className="bl-removal-confirm"
                onClick={spy.confirmBlacklistRemoval}
                disabled={spy.removingBlacklistContacts}
              >
                {spy.removingBlacklistContacts
                  ? <><i className="fa fa-spinner fa-spin" /> Removing...</>
                  : <><i className="fa fa-trash" /> Remove {spy.blacklistRemovalQueue.length}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <InstagramResearchModal
        isOpen={igResearchContact !== null}
        onClose={() => setIgResearchContact(null)}
        contactData={igResearchContact}
        userId={userId}
      />

      <SeoAnalyzerModal
        isOpen={seoResearchContact !== null}
        onClose={() => setSeoResearchContact(null)}
        contactData={seoResearchContact}
        userId={userId}
        onContactUpdate={(contactId, updates) => {
          setDbData(prev => prev.map(row =>
            row.id === contactId ? { ...row, ...updates } : row
          ));
          setSeoResearchContact(prev =>
            prev && prev.id === contactId ? { ...prev, ...updates } : prev
          );
        }}
      />

      <BulkResearchModal
        isOpen={bulkResearchModal.isOpen}
        autoStartDmFinder={bulkResearchModal.autoStartDmFinder}
        autoFinish={bulkResearchModal.autoFinish}
        funnelStats={bulkResearchModal.funnelStats}
        onClose={() => setBulkResearchModal({ isOpen: false, contacts: [] })}
        contacts={bulkResearchModal.contacts}
        userId={userId}
        research={research}
        researchLoading={researchLoading}
        researchContext={researchContext}
        seoKeyReady={seoKeyReady}
        potentialEnabled={potential.paSettings?.enabled === true}
        hasDecisionMakerResearch={(spy.spySettings?.activeModules || []).includes('decisionMakers')}
        onStartWebSpy={(contacts) => {
          // Plain Web Research: keep the manual review (clear any funnel/combo state).
          comboPendingRef.current = null;
          funnelAutoConfirmRef.current = false;
          setBulkResearchModal({ isOpen: false, contacts: [] });
          spy.handleStartMassSpy(contacts.map(c => c.id), contacts.length, creditBalance);
        }}
        onStartCombo={(webContacts, allContacts, opts) => {
          // Remember which contacts to run the DM Email Finder on after the
          // Web Research has applied its results, then kick off Web Research.
          comboPendingRef.current = (allContacts || []).map(c => c.id);
          // The Smart Funnel passes { autoConfirm: true } for a true one-click
          // run — the Mass Spy review is then auto-confirmed (see effect above).
          funnelAutoConfirmRef.current = opts?.autoConfirm === true;
          funnelStatsRef.current = opts?.funnelStats || null;
          setBulkResearchModal({ isOpen: false, contacts: [] });
          spy.handleStartMassSpy(webContacts.map(c => c.id), webContacts.length, creditBalance);
        }}
        onStartWebsiteAnalyzer={(contactIds) => {
          setBulkResearchModal({ isOpen: false, contacts: [] });
          analyzer.handleStartBulkAnalyze(contactIds, creditBalance);
        }}
        onStartPotential={(contactIds, profileId) => {
          setBulkResearchModal({ isOpen: false, contacts: [] });
          potential.handleStartBulkPotential(contactIds, creditBalance, profileId);
        }}
        analyzerCreditCost={analyzer.waSettings?.activeModules ? calculateWebsiteAnalyzerCost(analyzer.waSettings.activeModules) : 1}
        onContactUpdate={(contactId, updates) => {
          if (updates?._deletedIds) {
            // Bulk delete in a single state update so the table re-renders once
            // instead of once per removed contact (the Smart Funnel "5× refresh").
            const removedSet = new Set(updates._deletedIds);
            setDbData(prev => prev.filter(row => !removedSet.has(row.id)));
          } else if (updates?._deleted) {
            setDbData(prev => prev.filter(row => row.id !== contactId));
          } else if (updates?._sparkle) {
            setDbData(prev => prev.map(row =>
              row.id === contactId ? { ...row, _sparkle: true } : row
            ));
            setTimeout(() => {
              setDbData(prev => prev.map(row =>
                row.id === contactId ? { ...row, _sparkle: false } : row
              ));
            }, 2500);
          } else {
            setDbData(prev => prev.map(row => {
              if (row.id !== contactId) return row;
              const merged = { ...row, ...updates };
              // Deep-merge researchIntelligence so a single research step (e.g.
              // SEO) doesn't wipe sibling data (e.g. instagramInsight) written
              // earlier in the same bulk run.
              if (updates.researchIntelligence) {
                merged.researchIntelligence = {
                  ...(row.researchIntelligence || {}),
                  ...updates.researchIntelligence,
                };
              }
              return merged;
            }));
          }
        }}
      />

      <IntelligenceErrorBoundary label="SimilarContactsPanel" silent>
        <SimilarContactsPanel
          show={similarPanel.show}
          target={similarPanel.target}
          results={similarPanel.results}
          loading={similarPanel.loading}
          onClose={() => setSimilarPanel(p => ({ ...p, show: false }))}
          onContactClick={handleSimilarContactClick}
        />
      </IntelligenceErrorBoundary>

      <FlowSelectModal
        contact={flowSelectModal ? dbData.find(c => c.id === flowSelectModal) : null}
        userId={userId}
        warmupFlows={warmupFlows}
        onClose={() => setFlowSelectModal(null)}
        onStartFlow={handleStartWarmupFlow}
        onStopFlow={handleStopWarmupFlow}
        onReactivate={handleReactivateContact}
        onCompleteStep={handleCompleteWarmupStep}
        completing={flowSelectModal ? completingFlowStep[flowSelectModal] : false}
      />

      {/* Bulk Flow Modal */}
      {bulkFlowModal && (
        <div className="warmup-flow-modal-overlay" onClick={() => setBulkFlowModal(false)}>
          <div className="warmup-flow-modal bulk-flow-modal" onClick={(e) => e.stopPropagation()}>
            <div className="warmup-flow-modal-header">
              <span className="modal-title">Start Warmup Flow</span>
              <button className="warmup-flow-modal-close" onClick={() => setBulkFlowModal(false)}>
                <i className="fa fa-times"></i>
              </button>
            </div>
            <div className="warmup-flow-modal-content">
              <p className="warmup-flow-select-label bulk-label">
                <i className="fa fa-users"></i>
                {Object.keys(selectedRows).filter(id => selectedRows[id]).length} contacts
              </p>
              <FlowPickerList
                flows={warmupFlows}
                onPick={(flow) => handleBulkStartFlow(flow.id)}
                disabled={bulkFlowStarting}
                emptyText="No flows configured"
              />
            </div>
          </div>
        </div>
      )}

      {/* Outreach Job Assign Modal */}
      {outreachJobModal.isOpen && (
        <div className="warmup-flow-modal-overlay" onClick={() => setOutreachJobModal({ isOpen: false, assigning: false })}>
          <div className="warmup-flow-modal bulk-flow-modal" onClick={(e) => e.stopPropagation()}>
            <div className="warmup-flow-modal-header">
              <span className="modal-title"><i className="fa fa-paper-plane" style={{ marginRight: 8, opacity: 0.6 }}></i>Add to Outreach Job</span>
              <button className="warmup-flow-modal-close" onClick={() => setOutreachJobModal({ isOpen: false, assigning: false })}>
                <i className="fa fa-times"></i>
              </button>
            </div>
            <div className="warmup-flow-modal-content">
              <p className="warmup-flow-select-label bulk-label">
                <i className="fa fa-users"></i>
                {Object.keys(selectedRows).filter(id => selectedRows[id]).length} contacts
              </p>
              <div className="flow-select-list">
                {(() => {
                  const activeJobs = Object.entries(outreachJobs).filter(([, j]) => j.status === "active" || j.status === "draft");
                  if (activeJobs.length === 0) return (
                    <div className="flow-empty"><i className="fa fa-info-circle"></i><span>No active or draft jobs available</span></div>
                  );
                  return activeJobs.map(([jId, j]) => (
                    <button key={jId} className="flow-option" onClick={() => handleAssignToOutreachJob(jId)} disabled={outreachJobModal.assigning}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
                        <span className="flow-option-name">
                          {j.name}
                          {j.deliveryMode === "prepare" && <span className="oj-assign-prepare">Prepare only</span>}
                        </span>
                        <span className="flow-option-meta" style={{ display: 'flex', gap: 8 }}>
                          <span>{j.steps?.length || 0} steps</span>
                          <span style={{ opacity: 0.5 }}>·</span>
                          <span className={j.status === "active" ? "oj-assign-active" : ""}>{j.status}</span>
                          {j.stats?.totalContacts > 0 && <><span style={{ opacity: 0.5 }}>·</span><span>{j.stats.totalContacts} contacts</span></>}
                        </span>
                      </div>
                      {outreachJobModal.assigning ? <i className="fa fa-spinner fa-spin"></i> : <i className="fa fa-plus"></i>}
                    </button>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Per-Stage Template Modal (single + bulk) */}
      {templateModal.isOpen && (
        <div className="warmup-flow-modal-overlay" onClick={() => setTemplateModal(prev => ({ ...prev, isOpen: false }))}>
          <div className="template-stage-modal" onClick={(e) => e.stopPropagation()}>
            <div className="template-stage-modal-header">
              <span className="modal-title">Assign Templates</span>
              <button className="warmup-flow-modal-close" onClick={() => setTemplateModal(prev => ({ ...prev, isOpen: false }))}>
                <i className="fa fa-times"></i>
              </button>
            </div>
            <div className="template-stage-modal-body">
              {templateModal.isBulk && (
                <p className="template-stage-modal-info">
                  <i className="fa fa-users"></i>
                  {Object.keys(selectedRows).filter(id => selectedRows[id]).length} contacts selected
                </p>
              )}
              {[
                { key: 'intro', label: 'Intro Email' },
                { key: 'follow_up_1', label: 'Follow Up 1' },
                { key: 'follow_up_2', label: 'Follow Up 2' },
                { key: 'follow_up_3', label: 'Follow Up 3' },
              ].map(stage => (
                <div key={stage.key} className="template-stage-row">
                  <label className="template-stage-label">{stage.label}</label>
                  <select
                    className="template-stage-select"
                    value={templateModal.stages[stage.key] || ''}
                    onChange={(e) => setTemplateModal(prev => ({ ...prev, stages: { ...prev.stages, [stage.key]: e.target.value } }))}
                  >
                    <option value="">Default</option>
                    {emailTemplates.map(t => <option key={t.id} value={t.id}>{t.topic}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="template-stage-modal-footer">
              <button className="template-stage-cancel-btn" onClick={() => setTemplateModal(prev => ({ ...prev, isOpen: false }))}>
                Cancel
              </button>
              <button className="template-stage-save-btn" onClick={handleSaveTemplateModal} disabled={bulkTemplateAssigning}>
                {bulkTemplateAssigning ? <><i className="fa fa-spinner fa-spin"></i> Saving...</> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <DecisionMakersModal
        isOpen={decisionMakersModal.isOpen}
        contactData={decisionMakersModal.contactData}
        decisionMakers={decisionMakersModal.decisionMakers}
        onClose={() => setDecisionMakersModal({ isOpen: false, contactData: null, decisionMakers: [] })}
        userId={userId}
        onContactUpdate={(contactId, updates) => {
          setDbData(prev => prev.map(row =>
            row.id === contactId ? { ...row, ...updates } : row
          ));
        }}
      />

      <SocialEditModal
        isOpen={socialEditModal.isOpen}
        rowId={socialEditModal.rowId}
        initialSocials={socialEditModal.socials}
        onClose={() => setSocialEditModal({ isOpen: false, rowId: null, socials: {} })}
        userId={userId}
        onContactUpdate={(contactId, updates) => {
          setDbData(prev => prev.map(row =>
            row.id === contactId ? { ...row, ...updates } : row
          ));
        }}
      />

      <NotesModal
        isOpen={notesModal.isOpen}
        contact={notesModal.contact}
        initialActivity={notesModal.activity}
        onClose={closeNotesModal}
        userId={userId}
        onContactUpdate={(contactId, updates) => {
          setDbData(prev => prev.map(row =>
            row.id === contactId ? { ...row, ...updates } : row
          ));
        }}
      />

      <LeadStatusDrawer
        contact={leadStatusContact}
        flows={warmupFlows}
        onClose={closeLeadStatus}
        onOpenNotes={openNotesModal}
        onAction={handleLeadStatusAction}
      />

      <IntelligenceErrorBoundary label="IntelligenceModal" silent resetKey={intelModal?.id || null} onError={() => setIntelModal(null)}>
        <IntelligenceModal row={intelModal} userId={userId} onClose={() => setIntelModal(null)} />
      </IntelligenceErrorBoundary>

      {deleteConfirmModal.show && (
        <div className="delete-confirm-overlay" onClick={cancelDelete}>
          <div className="delete-confirm-modal delete-confirm-modal--extended" onClick={e => e.stopPropagation()}>
            <div className="delete-confirm-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="11" stroke="#ef4444" strokeWidth="1.5" fill="#fef2f2" />
                <path d="M12 8v4" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" />
                <circle cx="12" cy="15.5" r="1" fill="#dc2626" />
              </svg>
            </div>
            <h3 className="delete-confirm-title">Delete Contacts</h3>
            <p className="delete-confirm-text">
              Are you sure you want to permanently delete <strong>{deleteConfirmModal.count}</strong> contact{deleteConfirmModal.count !== 1 ? 's' : ''}? This action cannot be undone.
            </p>

            {deleteConfirmModal.emails?.length > 0 && (
              <div className="delete-confirm-emails">
                <div className="delete-confirm-emails-header">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                  <span>{deleteConfirmModal.emails.length} email address{deleteConfirmModal.emails.length !== 1 ? 'es' : ''}</span>
                </div>
                <div className="delete-confirm-emails-list">
                  {deleteConfirmModal.emails.map((email, i) => (
                    <div key={i} className="delete-confirm-email-item">{email}</div>
                  ))}
                </div>
              </div>
            )}

            {deleteConfirmModal.emails?.length > 0 && (
              <label className="delete-confirm-blacklist-toggle" onClick={() => setBlacklistOnDelete(prev => !prev)}>
                <div className={`delete-confirm-checkbox ${blacklistOnDelete ? 'checked' : ''}`}>
                  {blacklistOnDelete && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  )}
                </div>
                <div className="delete-confirm-blacklist-label">
                  <span className="delete-confirm-blacklist-title">Also add to Blacklist</span>
                  <span className="delete-confirm-blacklist-desc">Prevents future emails from being sent to these addresses</span>
                </div>
              </label>
            )}

            <div className="delete-confirm-actions">
              <button 
                className="delete-confirm-cancel" 
                onClick={cancelDelete}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                className={`delete-confirm-submit ${isDeleting ? 'deleting' : ''}`}
                onClick={() => confirmDelete(blacklistOnDelete)}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className="delete-modal-spinner"></span>
                    {blacklistOnDelete ? 'Deleting & Blacklisting...' : 'Deleting...'}
                  </>
                ) : (
                  <>
                    {blacklistOnDelete ? (
                      <>Delete & Blacklist {deleteConfirmModal.count} Contact{deleteConfirmModal.count !== 1 ? 's' : ''}</>
                    ) : (
                      <>Delete {deleteConfirmModal.count} Contact{deleteConfirmModal.count !== 1 ? 's' : ''}</>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase E-Fix (2026-08): Inline-Duplikat-Hint nach lead_email-Edit.
          Kleine Toast unten rechts. Server-Trigger legt zusätzlich einen
          persistenten Alert an — dieser Hint ist der unmittelbare Feedback-
          Kanal am Ort des Fehlers. */}
      <DuplicateInlineHint
        hint={duplicateHint}
        onOpen={(otherId) => {
          openContactById(otherId);
          setDuplicateHint(null);
        }}
        onMerge={() => {
          if (!duplicateHint) return;
          setInlineMergeCtx({
            contactIds: [duplicateHint.rowId, ...duplicateHint.matches.map(m => m.id)],
          });
          setDuplicateHint(null);
        }}
        onDismiss={() => setDuplicateHint(null)}
      />

      {(duplicateActionCtx || inlineMergeCtx) && (
        <DuplicateFlowStage
          step={dupFlowStep}
          lockBackdrop={dupFlowBusy}
          onBackdrop={() => {
            if (dupFlowTimer.current) window.clearTimeout(dupFlowTimer.current);
            setDuplicateActionCtx(null);
            setInlineMergeCtx(null);
          }}
          action={duplicateActionCtx ? (
            <DuplicateActionModal
              embedded
              userId={userId}
              alert={duplicateActionCtx}
              onClose={() => {
                if (dupFlowTimer.current) window.clearTimeout(dupFlowTimer.current);
                setDuplicateActionCtx(null);
                setInlineMergeCtx(null);
              }}
              onOpenContact={handleOpenContactDetail}
              onStartMerge={(contactIds, contactMap) => {
                if (dupFlowTimer.current) window.clearTimeout(dupFlowTimer.current);
                const same = inlineMergeCtx && Array.isArray(inlineMergeCtx.contactIds)
                  && inlineMergeCtx.contactIds.join('|') === contactIds.join('|');
                if (same) {
                  setDupFlowStep('merge');
                  return;
                }
                setInlineMergeCtx({
                  contactIds,
                  alertId: duplicateActionCtx.id,
                  returnAlert: duplicateActionCtx,
                  initialContacts: contactMap || null,
                });
              }}
            />
          ) : null}
          merge={inlineMergeCtx ? (
            <DuplicateMergeModal
              embedded
              userId={userId}
              alertId={inlineMergeCtx.alertId || null}
              contactIds={inlineMergeCtx.contactIds}
              initialContacts={inlineMergeCtx.initialContacts || null}
              onBusy={setDupFlowBusy}
              onClose={() => {
                if (dupFlowTimer.current) window.clearTimeout(dupFlowTimer.current);
                setDuplicateActionCtx(null);
                setInlineMergeCtx(null);
              }}
              onBack={inlineMergeCtx.returnAlert
                ? () => setDupFlowStep('action')
                : undefined}
              onMerged={() => {
                if (dupFlowTimer.current) window.clearTimeout(dupFlowTimer.current);
                setDuplicateActionCtx(null);
                setInlineMergeCtx(null);
              }}
            />
          ) : null}
        />
      )}

      {contactDetailCtx && (
        <ContactDetailModal
          contact={contactDetailCtx}
          onClose={() => setContactDetailCtx(null)}
        />
      )}

      {/* One-shot fatal-error modal for Outreach Jobs — pops up here in the
          WorldDb so the user sees the pause immediately, instead of only
          finding it inside Settings → Outreach Jobs. Reads/dismisses via the
          RTDB alerts node; a dismissed alert never re-shows. */}
      <OutreachJobPausedModal userId={userId} />
    </div>
  );
};

export default NewWorldDbTable; 